import { api } from "../api";
import { openModal, closeModal, modalBody, onModalClose } from "../components/modal";
import { confirmDialog, choiceDialog } from "../components/confirm-dialog";
import { renderWeekRangePicker } from "../components/week-range-picker";
import { initDropdownCheck, initDropdownSelect, type DropdownCheckHandle, type DropdownSelectHandle } from "../components/dropdown-check";
import { initSuggestInput, type SuggestInputHandle } from "../components/suggest-input";
import { Icons } from "../utils/icons";
import { exportGesamt, exportSchueler, exportPraktikumsstellen, exportKreisverband, type PlanningExportData, type SchuelerContactOptions, type StudentMasterDataOptions } from "../utils/export-service";
import { canWrite, writeOnly, lockInputs } from "../utils/permissions";
import { vacationAppliesTo } from "../utils/vacations";
import { vacationSplitDialog } from "../components/vacation-split-dialog";
import { studentVacationWeeks, spanHitsVacation, vacationsInSpan, splitExtend, splitShorten, newOverlaps, type WeekSpan } from "../utils/vacation-split";
import type { Student, SchoolClass, Practice, Department, DepartmentGroup, PlanningPeriod, Assignment, Vacation, ProposedAssignment, AutoPlanResult, Kreisverband } from "../types";
import { esc } from "../utils/html";

let periods: PlanningPeriod[] = [], activePeriod: PlanningPeriod | null = null;
let students: Student[] = [], classes: SchoolClass[] = [], practices: Practice[] = [], departments: Department[] = [];
let departmentGroups: DepartmentGroup[] = [];
let groupDeptIds = new Map<number, Set<string>>();       // public groupId → Set<departmentId>
let customGroupDeptIds = new Map<number, Set<string>>(); // custom groupId → Set<departmentId>
let kapazitaetProZeitraumEnabled = false;
let vorplanungEnabled = false;
let vorplanungDefaultVisible = false;
let vorplanungDefaultPreliminary = false;
let kreisverbandEnabled = false;
let kreisverbaende: Kreisverband[] = [];
let abrechnungEnabled = false;
let assignments: Assignment[] = [], vacations: Vacation[] = [];
interface WeekInfo { index: number; kw: number; year: number; isYearStart: boolean; label: string; dateRange: string; monday: Date; }
let weeks: WeekInfo[] = [];
let filterClasses: (number | "none")[] = []; // leer = alle Klassen; "none" = Schüler ohne Klassenzuordnung
let classDropdown: DropdownCheckHandle | null = null;
let periodDropdown: DropdownSelectHandle | null = null;
let filterKreisverband: number | "none" | undefined;
// Per Schülersuche gewählter Schüler; wirkt zusätzlich zu Klassen- und Kreisverband-Filter
let filterStudentId: number | null = null;
let studentSearch: SuggestInputHandle | null = null;
let previewAssignments: ProposedAssignment[] | null = null;
let previewStats: AutoPlanResult["stats"] | null = null;
let previewWarnings: string[] = [];
let previewConfig: any = null; // Config für Bestätigung
// Zeitraum, für den die Vorschau berechnet wurde. "Übernehmen" speichert immer dorthin,
// nie in einen inzwischen gewählten anderen Zeitraum.
let previewPeriodId: number | null = null;
// Bei einer Neuplanung: bestehende Einsätze, die "Übernehmen" löscht
let previewReplacedIds = new Set<number>();

// ── Ansicht (je Browser) ──
// Spaltenbreite und Namensreihenfolge sind persönliche Vorlieben, deshalb im localStorage
// statt an der Schule. Die Reihenfolge der Schüler bleibt die der API (Nachname, Vorname).
type Spaltenbreite = "normal" | "schmal" | "kompakt";
type Namensfolge = "nachname" | "vorname";
interface Ansicht { breite: Spaltenbreite; namen: Namensfolge; }
const ANSICHT_KEY = "planning.ansicht";
const COL_W: Record<Spaltenbreite, number> = { normal: 180, schmal: 110, kompakt: 48 };

function loadAnsicht(): Ansicht {
  const fallback: Ansicht = { breite: "normal", namen: "nachname" };
  try {
    const raw = JSON.parse(localStorage.getItem(ANSICHT_KEY) || "null");
    if (!raw || typeof raw !== "object") return fallback;
    return {
      breite: raw.breite in COL_W ? raw.breite : fallback.breite,
      namen: raw.namen === "vorname" ? "vorname" : fallback.namen,
    };
  } catch { return fallback; }
}
let ansicht: Ansicht = { breite: "normal", namen: "nachname" }; // render() lädt die gespeicherte Wahl

function saveAnsicht() {
  try { localStorage.setItem(ANSICHT_KEY, JSON.stringify(ansicht)); } catch { /* ohne Speicher gilt die Wahl nur bis zum Neuladen */ }
}

/** Breite einer Schülerspalte; im zellweisen Modus rechnet die Scrollposition damit. */
function colW(): number { return COL_W[ansicht.breite]; }

function studentLabel(s: Student): string {
  return ansicht.namen === "nachname"
    ? [s.lastName, s.firstName].filter(Boolean).join(", ")
    : [s.firstName, s.lastName].filter(Boolean).join(" ");
}

function clearPreview() {
  previewAssignments = null; previewStats = null; previewWarnings = []; previewConfig = null; previewPeriodId = null;
  previewReplacedIds = new Set();
}

/** Nach Anlegen, Bearbeiten oder Löschen eines Zeitraums passt eine offene Vorschau nicht
 *  mehr (andere Wochen oder anderer Zeitraum). Eine Rückfrage ist aus dem Modal heraus
 *  nicht darstellbar, deshalb verwerfen und Bescheid geben. */
function dropPreviewAfterPeriodChange() {
  if (!previewAssignments) return;
  clearPreview();
  showToast("Die Vorschau der automatischen Planung wurde verworfen.");
}

// ── Hilfsfunktionen Kalender ──

/** Donnerstag der ISO-Woche von d. Bestimmt sowohl Wochennummer als auch Wochenjahr. */
function isoThursday(d: Date): Date {
  const dt = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  dt.setUTCDate(dt.getUTCDate() + 4 - (dt.getUTCDay() || 7));
  return dt;
}
function getWeekNumber(d: Date): number {
  const dt = isoThursday(d);
  const y = new Date(Date.UTC(dt.getUTCFullYear(), 0, 1));
  return Math.ceil(((dt.getTime() - y.getTime()) / 86400000 + 1) / 7);
}
/** ISO-Wochenjahr: weicht am Jahreswechsel vom Kalenderjahr des Montags ab. */
function getWeekYear(d: Date): number { return isoThursday(d).getUTCFullYear(); }
function mondayOfWeek(yr: number, wk: number): Date {
  const j4 = new Date(yr, 0, 4), dow = j4.getDay() || 7, m = new Date(j4);
  m.setDate(j4.getDate() - dow + 1 + (wk - 1) * 7); return m;
}
/** Timezone-sicheres Parsen von "YYYY-MM-DD" bzw. ISO-Timestamps (verhindert UTC→CET Versatz). */
function parseLocal(s: string): Date {
  const [y, m, d] = String(s).slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d);
}
/** Tagesindex des Montags der Woche von d, in ganzen Tagen (zeitzonen- und sommerzeitfest). */
function mondayIndex(d: Date): number {
  const dayIndex = Math.floor(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86400000);
  // 1970-01-01 war ein Donnerstag, also hat der Montag den Rest 0 bei ((index + 3) % 7).
  return dayIndex - ((dayIndex + 3) % 7);
}
/** Anzahl ganzer Wochen von a nach b (Montag zu Montag). Entspricht dem Abstand zweier
 *  Spalten im Grid, weil weeks[] ebenfalls ab dem Montag des Startdatums hochzählt. */
function weekDiff(a: Date, b: Date): number {
  return (mondayIndex(b) - mondayIndex(a)) / 7;
}
/** Wochenzeitraum Mo bis So: "short" fürs Grid (dort steht das Jahr separat), "long" für die Dropdowns. */
function weekDates(mon: Date): { short: string; long: string } {
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
  const dm = (d: Date) => `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.`;
  const dmy = (d: Date) => `${dm(d)}${d.getFullYear()}`;
  return { short: `${dm(mon)} – ${dm(sun)}`, long: `${dmy(mon)} – ${dmy(sun)}` };
}

function mondayOf(d: Date): Date {
  const r = new Date(d); const day = r.getDay();
  r.setDate(r.getDate() + (day === 0 ? -6 : 1 - day)); r.setHours(0, 0, 0, 0); return r;
}
/** "YYYY-MM-DD" aus den lokalen Feldern (toISOString würde nach UTC verschieben). */
function isoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
/** Montag der Woche i im aktiven Zeitraum. i darf außerhalb von weeks[] liegen, etwa bei
 *  Ferien für alle Zeiträume, die über den Zeitraum hinausreichen. */
function mondayAt(i: number): Date {
  const m = mondayOf(parseLocal(activePeriod!.startDate));
  m.setDate(m.getDate() + i * 7);
  return m;
}
/** Beschriftung der Woche i wie in den Wochen-Dropdowns, auch außerhalb des Zeitraums. */
function weekLabelAt(i: number): string {
  const w = weeks[i];
  if (w) return w.label;
  const mon = mondayAt(i);
  return `KW ${getWeekNumber(mon)} · ${weekDates(mon).long}`;
}

// ── Ferien ──
// Ferien des Zeitraums und Ferien für alle Zeiträume kommen in einer Liste, haben aber
// getrennte id-Räume. Im DOM deshalb ein eigener Schlüssel statt der nackten id.
function vacKey(v: Vacation): string { return v.shared ? `s${v.id}` : String(v.id); }
function findVac(key: string | undefined): Vacation | undefined { return vacations.find(v => vacKey(v) === key); }

/** Rückfrage beim Löschen von Ferien für alle Zeiträume: nur hier oder überall.
 *  true, wenn gelöscht wurde; vacations ist dann neu geladen. */
async function deleteSharedVacation(v: Vacation, student?: Student): Promise<boolean> {
  const current = activePeriod!;
  const used = v.shared?.periods ?? [];
  const list = used.map(p => `• ${p.name}${+p.id === +current.id ? " (aktueller Zeitraum)" : ""}`).join("\n");
  const usage = used.length > 1
    ? `„${v.name}" gilt für alle Zeiträume und wird in ${used.length} Zeiträumen verwendet:\n${list}`
    : `„${v.name}" gilt für alle Zeiträume, wird derzeit aber nur im aktuellen Zeitraum „${current.name}" verwendet. `
      + `Später angelegte Zeiträume mit diesen Wochen übernehmen die Ferien ebenfalls.`;
  const studentName = student ? `${student.firstName} ${student.lastName}` : "";
  const options = student
    ? `Du kannst die Ferien nur für ${studentName} in diesem Zeitraum entfernen, nur aus dem aktuellen Zeitraum „${current.name}" entfernen oder in allen Zeiträumen löschen.`
    : `Du kannst die Ferien nur aus dem aktuellen Zeitraum „${current.name}" entfernen oder in allen Zeiträumen löschen.`;
  const choice = await choiceDialog({
    title: "Ferien für alle Zeiträume löschen",
    message: `${usage}\n\n${options}`,
    choices: [
      // Nur vom X am Block eines Schülers aus; im Menü ist kein Schüler gewählt
      ...(student ? [{ key: "student" as const, label: `Nur für ${studentName}`, truncate: true }] : []),
      { key: "period" as const, label: "Nur aus diesem Zeitraum entfernen" },
      { key: "all" as const, label: "In allen Zeiträumen löschen", danger: true },
    ],
  });
  if (!choice) return false;
  try {
    if (choice === "all") await api.sharedVacations.delete(v.id);
    else if (choice === "student") await api.sharedVacations.excludeStudent(v.id, current.id, student!.id);
    else await api.sharedVacations.excludeFromPeriod(v.id, current.id);
  } catch (err: any) { showToast(`Löschen fehlgeschlagen: ${err.message}`); return false; }
  vacations = await api.vacations.list(current.id);
  return true;
}

// ── Daten laden ──

async function loadAll() {
  // Schüler hängen nicht am Zeitraum und werden deshalb nur hier geladen, nicht bei jedem Zeitraumwechsel
  const [cls, depts, per, prac, s, grps, stud] = await Promise.all([
    api.classes.list(), api.departments(), api.periods.list(), api.practices.list(), api.settings.get(), api.settings.departmentGroups(),
    api.students.list(),
  ]);
  classes = cls; departments = depts; periods = per; practices = prac; departmentGroups = grps; students = stud;
  kapazitaetProZeitraumEnabled = s.kapazitaetProZeitraumEnabled ?? false;
  vorplanungEnabled = s.vorplanungEnabled ?? false;
  vorplanungDefaultVisible = s.vorplanungDefaultVisible ?? false;
  vorplanungDefaultPreliminary = s.vorplanungDefaultPreliminary ?? false;
  kreisverbandEnabled = s.drkKreisverbandEnabled ?? false;
  abrechnungEnabled = s.abrechnungEnabled ?? false;
  // Kreisverband-Liste nur für freigeschaltete Schulen laden (fürs Filter-Dropdown)
  if (kreisverbandEnabled) {
    try { kreisverbaende = await api.kreisverbaende.list(); } catch { kreisverbaende = []; }
  } else {
    kreisverbaende = []; filterKreisverband = undefined;
  }
  // Gruppe→Fachbereich-Maps aufbauen (public und custom getrennt, da IDs aus verschiedenen Tabellen)
  groupDeptIds = new Map(grps.filter(g => !g.isCustom).map(g => [+g.id, new Set((g.departments || []).map(d => d.id))]));
  customGroupDeptIds = new Map(grps.filter(g => g.isCustom).map(g => [+g.id, new Set((g.departments || []).map(d => d.id))]));

  const previous = activePeriod && periods.find(p => p.id === activePeriod!.id);
  const toLoad = previous || periods.find(p => p.active) || periods[0];
  if (toLoad) { activePeriod = toLoad; await loadPeriodData(toLoad); }
}

// Laufnummer des zuletzt angestoßenen Ladens. Wird schnell hintereinander gewechselt,
// darf eine später eintreffende Antwort eines älteren Zeitraums nichts mehr überschreiben.
let periodLoadSeq = 0;

/** Lädt Einsätze und Ferien eines Zeitraums und macht ihn zum aktiven. Wochen, Daten und
 *  activePeriod werden erst gemeinsam gesetzt, wenn die Antworten da sind. false, wenn
 *  inzwischen ein anderer Zeitraum angefordert wurde und das Ergebnis verworfen ist. */
async function loadPeriodData(p: PlanningPeriod): Promise<boolean> {
  const seq = ++periodLoadSeq;
  const startDate = parseLocal(p.startDate), endDate = parseLocal(p.endDate);
  const newWeeks: WeekInfo[] = [];
  let mon = mondayOf(startDate), i = 0;
  while (mon <= endDate) {
    const kw = getWeekNumber(mon), year = getWeekYear(mon);
    const { short, long } = weekDates(mon);
    // Jahresgrenze markieren, aber nicht in der ersten Zeile: dort grenzt bereits der Tabellenkopf an.
    const isYearStart = newWeeks.length > 0 && newWeeks[newWeeks.length - 1].year !== year;
    newWeeks.push({ index: i, kw, year, isYearStart, label: `KW ${kw} · ${long}`, dateRange: short, monday: new Date(mon) });
    const next = new Date(mon); next.setDate(next.getDate() + 7); mon = next; i++;
  }
  let a: Assignment[], v: Vacation[];
  try {
    [a, v] = await Promise.all([api.assignments.list(p.id), api.vacations.list(p.id)]);
  } catch (err) {
    // Ein Fehler beim inzwischen abgewählten Zeitraum betrifft niemanden mehr
    if (seq !== periodLoadSeq) return false;
    throw err;
  }
  if (seq !== periodLoadSeq) return false;
  activePeriod = p; weeks = newWeeks; assignments = a; vacations = v;
  return true;
}

/** Schüler aus Klassen- und Kreisverband-Filter, beide lokal angewendet. Grundlage der
 *  Schülersuche und der Formulare: "Ferien für alle Schüler" soll nicht auf einen
 *  einzelnen gesuchten Schüler schrumpfen. */
function filteredStudents(): Student[] {
  let list = students;
  if (filterClasses.length > 0) {
    const ids = new Set(filterClasses.filter(v => v !== "none") as number[]);
    const ohneKlasse = filterClasses.includes("none");
    list = list.filter(s => (s.classId ? ids.has(+s.classId) : ohneKlasse));
  }
  if (kreisverbandEnabled && filterKreisverband !== undefined) {
    list = filterKreisverband === "none"
      ? list.filter(s => !s.drkKreisverbandId)
      : list.filter(s => +(s.drkKreisverbandId ?? 0) === filterKreisverband);
  }
  return list;
}

/** Im Grid sichtbare Schüler: die gefilterten, bei aktiver Schülersuche nur der gewählte.
 *  Auch die Downloads folgen dieser Liste. */
function visibleStudents(): Student[] {
  const list = filteredStudents();
  return filterStudentId === null ? list : list.filter(s => +s.id === filterStudentId);
}

/** Fällt der gesuchte Schüler nach einer Filteränderung aus der Auswahl heraus,
 *  wird die Suche aufgehoben, statt ein leeres Raster zu zeigen. */
function dropStudentSearchIfFiltered() {
  if (filterStudentId === null) return;
  if (filteredStudents().some(s => +s.id === filterStudentId)) return;
  filterStudentId = null;
  studentSearch?.clear();
}

/** Name im Zeitraum-Dropdown. Ausgeblendete Zeiträume sind gekennzeichnet, damit die
 *  Schule sieht, was ihre Schüler nicht sehen. */
function periodLabel(p: PlanningPeriod): string {
  return p.visibleToStudents === false ? `${p.name} (für Schüler ausgeblendet)` : p.name;
}

/** Baut die Eintraege des Zeitraum-Dropdowns neu und markiert den aktiven. Wird nach
 *  jedem Anlegen, Bearbeiten und Loeschen eines Zeitraums aufgerufen. */
function fillPeriodDropdown(root: HTMLElement) {
  const panel = root.querySelector("#filter-period .dropdown-check-panel") as HTMLElement | null;
  if (!panel) return;
  panel.innerHTML = periods.map(p => `<div class="dropdown-check-item" data-value="${p.id}">${esc(periodLabel(p))}</div>`).join("");
  periodDropdown?.setValue(activePeriod ? String(activePeriod.id) : "");
}

/** Setzt den Klassenfilter auf die Vorgabe des Zeitraums. Ohne Vorgabe: alle Klassen.
 *  Nicht mehr existierende Klassen werden verworfen, da Klassen nachträglich
 *  gelöscht oder deaktiviert werden können. */
function applyPeriodClassDefault(p: PlanningPeriod | null) {
  const vorhanden = new Set(classes.map(c => +c.id));
  filterClasses = (p?.defaultClassIds || []).map(Number).filter(id => vorhanden.has(id));
  classDropdown?.setValues(filterClasses.map(String));
  dropStudentSearchIfFiltered();
}

// ── Spalten-Layout für überlappende Einsätze ──

function computeColumnLayout(
  studentAssignments: Assignment[]
): Map<number, { col: number; total: number }> {
  const layout = new Map<number, { col: number; total: number }>();
  const sorted = [...studentAssignments].sort(
    (a, b) => a.startWeek !== b.startWeek ? a.startWeek - b.startWeek : a.id - b.id
  );

  for (const a of sorted) {
    const aEnd = a.startWeek + a.durationWeeks;
    const overlapping = sorted.filter(b => {
      if (b.id === a.id) return false;
      return a.startWeek < b.startWeek + b.durationWeeks && aEnd > b.startWeek;
    });

    const usedCols = new Set(
      overlapping.map(b => layout.get(b.id)?.col).filter((c): c is number => c !== undefined)
    );
    let col = 0;
    while (usedCols.has(col)) col++;

    let maxOverlap = 1;
    for (let w = a.startWeek; w < aEnd; w++) {
      const n = studentAssignments.filter(
        b => b.startWeek <= w && b.startWeek + b.durationWeeks > w
      ).length;
      maxOverlap = Math.max(maxOverlap, n);
    }

    layout.set(a.id, { col, total: maxOverlap });
  }
  return layout;
}

// ── Grid rendern ──

/** Höhe einer Wochenzeile im Raster; Blockhöhen rechnen damit. */
const ROW_H = 64;
/** Feste Breite der Wochenspalte im zellweisen Modus; die Schülerspalten folgen der Ansicht. */
const CW_WEEK_W = 120;
/** Ab so vielen Zellen (Wochen × Schüler) springt das Raster zellweise und baut nur den
 *  sichtbaren Ausschnitt; darunter scrollt es frei wie eine normale Tabelle. Bei 100
 *  Schülern über ein Jahr hatte der Browser beim Scrollen mit Tausenden Zellen, sticky
 *  Kopf und sticky Wochenspalte zu tun. */
const CW_MIN_CELLS = 2000;

interface StudentCells {
  vacs: Vacation[];
  assigns: Assignment[];
  previews: ProposedAssignment[];
  vacStarts: Map<number, Vacation[]>;
  assignStarts: Map<number, Assignment[]>;
  previewStarts: Map<number, ProposedAssignment[]>;
  covered: Uint8Array;
  layout: Map<number, { col: number; total: number }>;
}
interface GridModel { students: Student[]; cells: Map<number, StudentCells>; deptById: Map<string, Department>; }

/** Einmal je Rendern vorsortieren, statt für jede Zelle alle Einsätze und Ferien zu
 *  durchsuchen: Das Raster hat Wochen × Schüler Zellen, bei mehrjährigen Zeiträumen
 *  Zehntausende. Gezeichnet wird ein Block in seiner Startwoche (im zellweisen Modus
 *  zusätzlich in der ersten sichtbaren Zeile, wenn er früher beginnt); "belegt" steuert
 *  die Klickfläche für neue Einsätze. Die Reihenfolge innerhalb einer Zelle bleibt
 *  die der geladenen Listen. */
function buildGridModel(gridStudents: Student[]): GridModel {
  const byStudent = <T extends { studentId?: number | null }>(list: T[]) => {
    const map = new Map<number, T[]>();
    for (const x of list) {
      const sid = +(x.studentId ?? 0);
      const arr = map.get(sid);
      if (arr) arr.push(x); else map.set(sid, [x]);
    }
    return map;
  };
  const assignByStudent = byStudent(assignments);
  const previewByStudent = byStudent(previewAssignments ?? []);
  const deptById = new Map<string, Department>();
  for (const d of departments) if (!deptById.has(d.id)) deptById.set(d.id, d);

  const groupByStart = <T extends { startWeek: number }>(list: T[]) => {
    const map = new Map<number, T[]>();
    for (const x of list) {
      const arr = map.get(x.startWeek);
      if (arr) arr.push(x); else map.set(x.startWeek, [x]);
    }
    return map;
  };
  const cells = new Map<number, StudentCells>();
  for (const s of gridStudents) {
    const sid = +s.id;
    const sa = assignByStudent.get(sid) ?? [];
    const sv = vacations.filter(v => vacationAppliesTo(v, sid));
    const sp = previewByStudent.get(sid) ?? [];
    const covered = new Uint8Array(weeks.length);
    const mark = (from: number, toExcl: number) => {
      for (let i = Math.max(0, from); i < Math.min(weeks.length, toExcl); i++) covered[i] = 1;
    };
    for (const a of sa) mark(a.startWeek, a.startWeek + a.durationWeeks);
    for (const v of sv) mark(v.startWeek, v.endWeek + 1);
    for (const p of sp) mark(p.startWeek, p.startWeek + p.durationWeeks);
    cells.set(sid, {
      vacs: sv, assigns: sa, previews: sp,
      vacStarts: groupByStart(sv), assignStarts: groupByStart(sa), previewStarts: groupByStart(sp),
      covered, layout: computeColumnLayout(sa),
    });
  }
  return { students: gridStudents, cells, deptById };
}

/** Höhe eines Blocks, der in Zeile `row` gezeichnet wird und vor `endExcl` endet. Beginnt er
 *  früher (nur im zellweisen Modus), sitzt er ohne oberen Abstand an der Zeilenkante. */
function blockHeight(row: number, endExcl: number, cont: boolean): number {
  return (endExcl - row) * ROW_H - (cont ? 4 : 8);
}

function vacationBlockHtml(v: Vacation, row: number): string {
  const cont = v.startWeek < row;
  const key = vacKey(v);
  const title = ansicht.breite !== "normal"
    ? `${v.name}${v.shared ? "\nGilt für alle Zeiträume" : ""}`
    : v.shared ? "Gilt für alle Zeiträume" : "";
  return `<div class="vacation-block${v.shared ? " vacation-block-shared" : ""}${cont ? " ab-cont" : ""}" data-vac="${key}" style="height:${blockHeight(row, v.endWeek + 1, cont)}px"${title ? ` title="${esc(title)}"` : ""}>
          ${Icons.sun} <span class="vb-name">${esc(v.name)}</span>
          ${v.shared ? `<span class="vb-shared-label">Alle Zeiträume</span>` : ""}
          ${writeOnly(`<span class="ab-close" data-del-vac="${key}">${Icons.xMark}</span>`)}
        </div>`;
}

function assignmentBlockHtml(a: Assignment, row: number, sc: StudentCells, deptById: Map<string, Department>): string {
  const cont = a.startWeek < row;
  const h = blockHeight(row, a.startWeek + a.durationWeeks, cont);
  const dept = deptById.get(a.department);
  const deptLabel = dept?.shortName || "";
  const nameLabel = a.practiceName || a.freeInputText || "";
  const lay = sc.layout.get(a.id) ?? { col: 0, total: 1 };
  const posStyle = lay.total > 1
    ? `left:calc(${(lay.col / lay.total) * 100}% + 2px);width:calc(${(1 / lay.total) * 100}% - 4px);right:auto;`
    : "";
  const notVisible = vorplanungEnabled && a.visibleToStudent === false;
  const isPreliminary = vorplanungEnabled && a.isPreliminary;
  const visibilityIcon = notVisible
    ? `<span class="ab-visibility" title="Nicht sichtbar für Schüler">${Icons.eyeSlash}</span>`
    : "";
  const preliminaryBadge = isPreliminary
    ? `<span class="ab-preliminary" title="Vorläufig">Vorläufig</span>`
    : "";
  const hasInternal = !!(a.internalNote && a.internalNote.trim());
  const hasStudent = !!(a.studentNote && a.studentNote.trim());
  const hasBilled = abrechnungEnabled && !!a.abgerechnet;
  const internalCommentIcon = hasInternal
    ? `<span class="ab-comment ab-comment-internal" title="Interner Kommentar: ${esc(a.internalNote!).replace(/"/g, "&quot;")}">${Icons.chatBubbleLeft}</span>`
    : "";
  const studentCommentIcon = hasStudent
    ? `<span class="ab-comment ab-comment-student" title="Kommentar für Schüler: ${esc(a.studentNote!).replace(/"/g, "&quot;")}">${Icons.chatBubbleLeftRight}</span>`
    : "";
  const billedIcon = hasBilled
    ? `<span class="ab-comment ab-billed" title="Abgerechnet">${Icons.banknotes}</span>`
    : "";
  const commentBadges = (hasInternal || hasStudent || hasBilled)
    ? `<div class="ab-comments">${internalCommentIcon}${studentCommentIcon}${billedIcon}</div>`
    : "";
  const replaced = !!previewAssignments && previewReplacedIds.has(+a.id);
  // In schmalen Spalten fehlt der Platz für Dauer, Kennzeichen und Kommentare; der
  // Tooltip trägt dann alles, was der Block nicht mehr zeigt.
  let title = replaced ? "Wird beim Übernehmen der Vorschau ersetzt" : "";
  if (ansicht.breite !== "normal") {
    title = [
      `${deptLabel}${deptLabel && nameLabel ? " · " : ""}${nameLabel}`,
      `${a.durationWeeks} Wochen · ${a.hoursPerWeek}h/Woche`,
      isPreliminary ? "Vorläufig" : "",
      notVisible ? "Nicht sichtbar für Schüler" : "",
      hasBilled ? "Abgerechnet" : "",
      hasInternal ? `Interner Kommentar: ${a.internalNote}` : "",
      hasStudent ? `Kommentar für Schüler: ${a.studentNote}` : "",
      title,
    ].filter(Boolean).join("\n");
  }
  return `<div class="assignment-block${notVisible ? " not-visible" : ""}${isPreliminary ? " preliminary" : ""}${replaced ? " replaced" : ""}${cont ? " ab-cont" : ""}" data-assign="${a.id}"${title ? ` title="${esc(title)}"` : ""} style="background:${a.departmentColor || "#6b7280"};height:${h}px;${posStyle}">
          ${visibilityIcon}
          <div class="ab-name">${esc(deptLabel)}${deptLabel && nameLabel ? " · " : ""}${esc(nameLabel)}</div>
          <div class="ab-meta"><span class="ab-meta-text">${a.durationWeeks}W · ${a.hoursPerWeek}h/Woche</span>${preliminaryBadge}</div>
          ${commentBadges}
          ${writeOnly(`<span class="ab-close" data-del-assign="${a.id}">${Icons.xMark}</span>`)}
        </div>`;
}

function previewBlockHtml(a: ProposedAssignment, row: number, deptById: Map<string, Department>): string {
  const cont = a.startWeek < row;
  const h = blockHeight(row, a.startWeek + a.durationWeeks, cont);
  const dept = deptById.get(a.department);
  const deptLabel = dept?.shortName || "";
  return `<div class="assignment-block preview${cont ? " ab-cont" : ""}" style="background:${a.departmentColor || "#6b7280"};height:${h}px">
            <div class="ab-name">${esc(deptLabel)}${deptLabel && a.practiceName ? " · " : ""}${esc(a.practiceName || "")}</div>
            <div class="ab-meta"><span class="ab-meta-text">${a.durationWeeks}W · ${a.hoursPerWeek}h/Woche</span></div>
          </div>`;
}

/** Zelle eines Schülers in Woche `wk`. `firstRow`: oberste gezeichnete Zeile im zellweisen
 *  Modus; dort erscheinen auch Blöcke, die oberhalb des Ausschnitts beginnen. */
function cellHtml(s: Student, wk: number, model: GridModel, firstRow: boolean): string {
  const sc = model.cells.get(+s.id)!;
  const vacs = firstRow ? sc.vacs.filter(v => v.startWeek <= wk && v.endWeek >= wk) : sc.vacStarts.get(wk);
  const assigns = firstRow ? sc.assigns.filter(a => a.startWeek <= wk && a.startWeek + a.durationWeeks > wk) : sc.assignStarts.get(wk);
  let html = "";
  vacs?.forEach(v => { html += vacationBlockHtml(v, wk); });
  assigns?.forEach(a => { html += assignmentBlockHtml(a, wk, sc, model.deptById); });
  if (previewAssignments) {
    const previews = firstRow ? sc.previews.filter(a => a.startWeek <= wk && a.startWeek + a.durationWeeks > wk) : sc.previewStarts.get(wk);
    previews?.forEach(a => { html += previewBlockHtml(a, wk, model.deptById); });
  }
  const isEmpty = !sc.covered[wk];
  return `<td class="${isEmpty ? "cell-empty" : ""}" data-student="${s.id}" data-week="${wk}">${html}</td>`;
}

/** Tabelle für die Wochen [r0, r1) und Schülerspalten [c0, c1). Im freien Modus das ganze
 *  Raster, im zellweisen Modus der sichtbare Ausschnitt. Feste Spaltenbreiten gelten im
 *  zellweisen Modus und in der schmalen und kompakten Ansicht, weil dort sonst lange Namen
 *  die Spalte wieder aufziehen würden. */
function gridTableHtml(model: GridModel, r0: number, r1: number, c0: number, c1: number, cellwise: boolean): string {
  const cols = model.students.slice(c0, c1);
  const narrow = ansicht.breite !== "normal";
  // Gekürzte Köpfe zeigen den vollen Namen im Tooltip; kompakt auch Ort und Auto
  const headTitle = (s: Student) => ansicht.breite === "kompakt"
    ? [studentLabel(s), s.ort || "", s.hasCar ? "Auto vorhanden" : ""].filter(Boolean).join("\n")
    : studentLabel(s);
  const thStudents = cols.map(s => `<th${cellwise || narrow ? ` title="${esc(headTitle(s))}"` : ""}>
      <div class="student-header">${esc(studentLabel(s))}</div>
      <div class="student-sub">${s.ort || "–"} ${s.hasCar ? "🚗" : ""}</div>
    </th>`).join("");
  const rows = weeks.slice(r0, r1).map(w => {
    const cells = cols.map(s => cellHtml(s, w.index, model, cellwise && w.index === r0)).join("");
    return `<tr class="${w.isYearStart ? "week-year-start" : ""}"><td>`
      + `<div class="kw-label">${w.dateRange}</div>`
      + `<div class="kw-dates"><span class="kw-year">${w.year}</span> · KW ${w.kw}</div>`
      + `</td>${cells}</tr>`;
  }).join("");
  if (!cellwise && !narrow) return `<table><thead><tr><th>Woche</th>${thStudents}</tr></thead><tbody>${rows}</tbody></table>`;
  const colgroup = `<colgroup><col style="width:${CW_WEEK_W}px">${cols.map(() => `<col style="width:${colW()}px">`).join("")}</colgroup>`;
  return `<table class="fixed-cols" style="width:${CW_WEEK_W + cols.length * colW()}px">${colgroup}<thead><tr><th>Woche</th>${thStudents}</tr></thead><tbody>${rows}</tbody></table>`;
}

// ── Zellweiser Modus ──
// Der Wrapper bleibt der native Scroll-Container (Scrollbalken, Rad, Trackpad, Touch,
// Tastatur). Ein Platzhalter gibt ihm die volle Scrollfläche, die Ansicht darin ist
// sticky und bewegt sich nie. Aus der Scrollposition folgen erste Woche und erster
// Schüler; nur wenn sich die ändern, wird der Ausschnitt neu gebaut.

interface Cellwise {
  wrapper: HTMLElement; sizer: HTMLElement; view: HTMLElement; model: GridModel;
  r0: number; c0: number; width: number; height: number; headH: number;
  rowsVis: number; colsVis: number; fullCols: number; maxR: number; maxC: number;
  frame: number;
}
let cellwise: Cellwise | null = null;
let gridResizeObserver: ResizeObserver | null = null;

function measureCellwise(cw: Cellwise) {
  const { wrapper, sizer, view, model } = cw;
  if (!cw.headH) {
    view.innerHTML = gridTableHtml(model, 0, 0, 0, 1, true);
    cw.headH = view.querySelector("thead")!.getBoundingClientRect().height;
  }
  // Zweiter Durchgang, falls erst der Platzhalter Scrollbalken einblendet und damit die Fläche verkleinert
  for (let pass = 0; pass < 2; pass++) {
    // innerHeight als Obergrenze: ohne begrenzte Höhe würde der Wrapper mit dem Platzhalter mitwachsen
    const w = wrapper.clientWidth, h = Math.min(wrapper.clientHeight, window.innerHeight);
    const bodyW = Math.max(0, w - CW_WEEK_W), bodyH = Math.max(0, h - cw.headH);
    cw.width = w; cw.height = h;
    cw.colsVis = Math.ceil(bodyW / colW()); cw.rowsVis = Math.ceil(bodyH / ROW_H);
    // Am Ende soll der letzte Schüler bzw. die letzte Woche ganz zu sehen sein
    cw.fullCols = Math.max(1, Math.floor(bodyW / colW()));
    cw.maxC = Math.max(0, model.students.length - cw.fullCols);
    cw.maxR = Math.max(0, weeks.length - Math.max(1, Math.floor(bodyH / ROW_H)));
    view.style.width = `${w}px`; view.style.height = `${h}px`;
    sizer.style.width = `${w + cw.maxC * colW()}px`; sizer.style.height = `${h + cw.maxR * ROW_H}px`;
    if (wrapper.clientWidth === w && Math.min(wrapper.clientHeight, window.innerHeight) === h) break;
  }
}

function paintCellwise(cw: Cellwise, force = false) {
  const clamp = (v: number, max: number) => Math.min(max, Math.max(0, v));
  const r0 = clamp(Math.round(cw.wrapper.scrollTop / ROW_H), cw.maxR);
  const c0 = clamp(Math.round(cw.wrapper.scrollLeft / colW()), cw.maxC);
  if (!force && r0 === cw.r0 && c0 === cw.c0) return;
  cw.r0 = r0; cw.c0 = c0;
  cw.view.innerHTML = gridTableHtml(cw.model, r0, Math.min(weeks.length, r0 + cw.rowsVis),
    c0, Math.min(cw.model.students.length, c0 + cw.colsVis), true);
}

function startCellwise(wrapper: HTMLElement, model: GridModel, keepScroll?: { left: number; top: number }) {
  const cw: Cellwise = {
    wrapper, sizer: wrapper.querySelector(".cw-sizer")!, view: wrapper.querySelector(".cw-view")!, model,
    r0: -1, c0: -1, width: 0, height: 0, headH: 0, rowsVis: 0, colsVis: 0, fullCols: 1, maxR: 0, maxC: 0, frame: 0,
  };
  cellwise = cw;
  measureCellwise(cw);
  if (keepScroll) { wrapper.scrollLeft = keepScroll.left; wrapper.scrollTop = keepScroll.top; }
  paintCellwise(cw, true);
  wrapper.addEventListener("scroll", () => {
    if (cw.frame) return;
    cw.frame = requestAnimationFrame(() => { cw.frame = 0; paintCellwise(cw); });
  }, { passive: true });
  // Fenster oder Seitenleiste ändern die Fläche; nach Verlassen der Seite meldet sich der Beobachter ab
  const ro = new ResizeObserver(() => {
    if (!wrapper.isConnected) { ro.disconnect(); return; }
    const { width, height } = cw;
    measureCellwise(cw);
    if (cw.width !== width || cw.height !== height) paintCellwise(cw, true);
  });
  ro.observe(wrapper);
  gridResizeObserver = ro;
}

/** Pfeil-Knöpfe: zellweise um eine Bildschirmbreite Schüler (eine Spalte bleibt als
 *  Orientierung stehen), frei wie bisher um 300 px. */
function scrollGridSideways(el: HTMLElement, dir: 1 | -1) {
  const w = el.querySelector<HTMLElement>(".planning-wrapper");
  if (!w) return;
  if (cellwise?.wrapper === w) w.scrollBy({ left: dir * Math.max(1, cellwise.fullCols - 1) * colW() });
  else w.scrollBy({ left: dir * 300, behavior: "smooth" });
}

function renderGridPreservingScroll(el: HTMLElement) {
  const wrapper = el.querySelector(".planning-wrapper") as HTMLElement | null;
  renderGrid(el, { left: wrapper?.scrollLeft ?? 0, top: wrapper?.scrollTop ?? 0 });
}

function renderGrid(el: HTMLElement, keepScroll?: { left: number; top: number }) {
  gridResizeObserver?.disconnect(); gridResizeObserver = null; cellwise = null;
  const wrap = el.querySelector("#planning-grid")!;
  if (!activePeriod) {
    wrap.innerHTML = `<div class="empty-state"><p>Kein Planungszeitraum vorhanden.</p>${writeOnly(`<button class="btn btn-primary" id="btn-first-period">Ersten Zeitraum anlegen</button>`)}</div>`;
    wrap.querySelector("#btn-first-period")?.addEventListener("click", openPeriodForm);
    return;
  }
  const gridStudents = visibleStudents();
  if (gridStudents.length === 0) { wrap.innerHTML = `<div class="empty-state"><p>Keine Schüler in der Auswahl.</p></div>`; return; }

  const model = buildGridModel(gridStudents);
  const useCellwise = weeks.length * gridStudents.length >= CW_MIN_CELLS;

  // Preview-Bar
  let previewBar = "";
  if (previewAssignments) {
    const warnHtml = previewWarnings.length > 0 ? `<a href="#" class="warning-badge" id="btn-show-warnings">${previewWarnings.length} Hinweis(e)</a>` : "";
    const vorplanungBtn = vorplanungEnabled
      ? `<button class="btn btn-secondary" id="btn-confirm-plan-vorplanung">Als Vorplanung übernehmen</button>`
      : "";
    previewBar = `<div class="preview-bar">
      <span><strong>Vorschau:</strong> ${previewStats?.assignmentsCreated || 0} neue Zuweisungen für ${previewStats?.studentsPlanned || 0} Schüler${previewReplacedIds.size > 0 ? `, ${previewReplacedIds.size} ${previewReplacedIds.size === 1 ? "bestehender Einsatz wird" : "bestehende Einsätze werden"} ersetzt` : ""}</span>
      ${warnHtml}
      <div style="margin-left:auto;display:flex;gap:.5rem">
        <button class="btn btn-primary" id="btn-confirm-plan">Übernehmen</button>
        ${vorplanungBtn}
        <button class="btn btn-secondary" id="btn-cancel-plan">Verwerfen</button>
      </div>
    </div>`;
  }

  const widthClass = ansicht.breite === "normal" ? "" : ` plan-w-${ansicht.breite}`;
  wrap.innerHTML = useCellwise
    ? `${previewBar}<div class="planning-wrapper cellwise${widthClass}"><div class="cw-sizer"><div class="cw-view"></div></div></div>`
    : `${previewBar}<div class="planning-wrapper${widthClass}">${gridTableHtml(model, 0, weeks.length, 0, gridStudents.length, false)}</div>`;

  // Preview-Buttons
  if (previewAssignments) {
    const doConfirmPlan = async (visibleToStudent: boolean, isPreliminary: boolean) => {
      if (!activePeriod || !previewAssignments || previewPeriodId === null) return;
      if (+activePeriod.id !== previewPeriodId) {
        showToast("Die Vorschau gehört zu einem anderen Zeitraum. Bitte neu berechnen.");
        return;
      }
      try {
        await api.autoPlanning.confirm(previewPeriodId, {
          assignments: previewAssignments, keepExisting: previewConfig?.keepExisting !== false,
          replaceAssignmentIds: [...previewReplacedIds], visibleToStudent, isPreliminary,
        });
        clearPreview();
        assignments = await api.assignments.list(activePeriod.id);
        renderGrid(el);
      } catch (err: any) { showToast(`Fehler beim Speichern: ${err.message}`); }
    };
    wrap.querySelector("#btn-confirm-plan")?.addEventListener("click", () => doConfirmPlan(true, false));
    wrap.querySelector("#btn-confirm-plan-vorplanung")?.addEventListener("click", () => doConfirmPlan(false, true));
    wrap.querySelector("#btn-cancel-plan")?.addEventListener("click", () => {
      clearPreview();
      renderGrid(el);
    });
    wrap.querySelector("#btn-show-warnings")?.addEventListener("click", (e) => {
      e.preventDefault();
      const items = previewWarnings.map(w => `<li>${esc(w)}</li>`).join("");
      openModal("Hinweise zur automatischen Planung", `<ul style="font-size:.875rem;display:flex;flex-direction:column;gap:.5rem;padding-left:1.25rem">${items}</ul><div class="form-actions"><button class="btn btn-secondary" id="f-close">Schließen</button></div>`);
      modalBody().querySelector("#f-close")!.addEventListener("click", closeModal);
    });
  }

  const tableWrap = wrap.querySelector(".planning-wrapper") as HTMLElement;
  if (useCellwise) startCellwise(tableWrap, model, keepScroll);
  else if (keepScroll) {
    requestAnimationFrame(() => {
      const w = el.querySelector(".planning-wrapper") as HTMLElement | null;
      if (w) { w.scrollLeft = keepScroll.left; w.scrollTop = keepScroll.top; }
    });
  }

  // Click: Delete-Buttons, Block-Edit und leere Zellen
  tableWrap.addEventListener("click", async (e) => {
    const target = e.target as HTMLElement;

    // Delete Assignment
    const delAssign = canWrite() ? target.closest("[data-del-assign]") as HTMLElement : null;
    if (delAssign) {
      e.stopPropagation();
      const ok = await confirmDialog({
        title: "Zuweisung löschen",
        message: "Soll dieser Einsatz gelöscht werden?",
        confirmLabel: "Löschen", danger: true,
      });
      if (!ok) return;
      try {
        await api.assignments.delete(+delAssign.dataset.delAssign!);
        assignments = await api.assignments.list(activePeriod!.id);
        renderGridPreservingScroll(el);
      } catch (err: any) { showToast(`Löschen fehlgeschlagen: ${err.message}`); }
      return;
    }

    // Delete Vacation
    const delVac = canWrite() ? target.closest("[data-del-vac]") as HTMLElement : null;
    if (delVac) {
      e.stopPropagation();
      const v = findVac(delVac.dataset.delVac);
      if (!v) return;
      if (v.shared) {
        // Das X sitzt in der Zeile eines Schülers: der wird als Wahl "nur für ihn" angeboten
        const sid = +((delVac.closest("td[data-student]") as HTMLElement | null)?.dataset.student ?? 0);
        if (await deleteSharedVacation(v, students.find(s => +s.id === sid))) renderGridPreservingScroll(el);
        return;
      }
      const ok = await confirmDialog({
        title: "Ferien löschen",
        message: "Sollen diese Ferien gelöscht werden?",
        confirmLabel: "Löschen", danger: true,
      });
      if (!ok) return;
      try {
        await api.vacations.delete(v.id);
        vacations = await api.vacations.list(activePeriod!.id);
        renderGridPreservingScroll(el);
      } catch (err: any) { showToast(`Löschen fehlgeschlagen: ${err.message}`); }
      return;
    }

    // Klick auf Assignment-Block → Edit-Modal
    const assignBlock = target.closest(".assignment-block[data-assign]") as HTMLElement;
    if (assignBlock) {
      const a = assignments.find(x => +x.id === +assignBlock.dataset.assign!);
      if (a) openAssignForm(+a.studentId, a.startWeek, a);
      return;
    }

    // Klick auf Vacation-Block → Edit-Modal
    const vacBlock = target.closest(".vacation-block[data-vac]") as HTMLElement;
    if (vacBlock) {
      const v = findVac(vacBlock.dataset.vac);
      if (v) openEditVacationForm(v);
      return;
    }

    // Leere Fläche → neue Zuweisung (für Betrachter gibt es nichts anzulegen)
    if (!canWrite()) return;
    const td = target.closest("td[data-student]") as HTMLElement;
    if (!td) return;
    const sid = +td.dataset.student!, wk = +td.dataset.week!;
    const hasContent = assignments.some(a => +a.studentId === sid && a.startWeek <= wk && a.startWeek + a.durationWeeks > wk)
      || vacations.some(v => vacationAppliesTo(v, sid) && v.startWeek <= wk && v.endWeek >= wk);
    if (!hasContent) openAssignForm(sid, wk);
  });
}

function getDeptsForStudent(sid: number): Department[] {
  const student = students.find(s => +s.id === sid);
  const cls = classes.find(c => c.id === student?.classId);
  if (!cls) return departments;
  let allowed: Set<string> | undefined;
  if (cls.departmentGroupId) allowed = groupDeptIds.get(cls.departmentGroupId);
  else if (cls.customGroupId) allowed = customGroupDeptIds.get(cls.customGroupId);
  if (!allowed) return departments;
  return departments.filter(d => allowed!.has(d.id));
}

/** Wochenbereich für Rückfragen, etwa "KW 40 bis KW 42". Läuft der Zeitraum über den
 *  Jahreswechsel, mit Jahr, sonst ist "KW 2" nach "KW 51" nicht zu verstehen. */
function weekRangeLabel(start: number, end: number): string {
  const multiYear = weeks.length > 0 && weeks[0].year !== weeks[weeks.length - 1].year;
  const one = (i: number) => {
    const w = weeks[i];
    if (!w) return `Woche ${i + 1}`;
    return multiYear ? `KW ${w.kw}/${w.year}` : `KW ${w.kw}`;
  };
  return start === end ? one(start) : `${one(start)} bis ${one(end)}`;
}

/** Läuft der Einsatz über Ferien des Schülers, wird gefragt, ob sie ausgespart werden
 *  sollen. Ergebnis: die zu speichernden Teile, "keep" für unverändert speichern oder
 *  null für Abbrechen. Beim Bearbeiten wird nur gefragt, wenn sich Schüler oder Wochen
 *  geändert haben, sonst käme die Frage bei jeder Kommentaränderung erneut. */
async function askVacationSplit(
  data: { studentId: number; startWeek: number; durationWeeks: number }, existing?: Assignment,
): Promise<WeekSpan[] | "keep" | null> {
  if (existing && +existing.studentId === +data.studentId && existing.startWeek === data.startWeek
    && existing.durationWeeks === data.durationWeeks) return "keep";
  const span: WeekSpan = { startWeek: data.startWeek, durationWeeks: data.durationWeeks };
  const vacWeeks = studentVacationWeeks(vacations, data.studentId);
  if (!spanHitsVacation(span, vacWeeks)) return "keep";

  const extend = splitExtend(span, vacWeeks, weeks.length);
  const shorten = splitShorten(span, vacWeeks, weeks.length);
  const choice = await vacationSplitDialog({
    span,
    vacations: vacationsInSpan(vacations, data.studentId, span),
    extend: { ...extend, overlaps: newOverlaps(extend.parts, span, assignments, data.studentId, existing?.id) },
    shorten,
    rangeLabel: weekRangeLabel,
  });
  if (choice === null || choice === "keep") return choice;
  const parts = choice === "extend" ? extend.parts : shorten;
  return parts.length ? parts : null;
}

async function openAssignForm(studentId: number, startWeek: number, existing?: Assignment) {
  const isEdit = !!existing;
  const allowedDepts = getDeptsForStudent(studentId);
  const buildDeptOpts = (depts: Department[], selectedDeptId?: string) =>
    depts.map(d => `<option value="${d.id}" ${(selectedDeptId || depts[0]?.id) === d.id ? "selected" : ""}>${d.nameDe}</option>`).join("");
  const deptOpts = buildDeptOpts(allowedDepts, existing?.department);
  const stuOpts = filteredStudents().map(s => `<option value="${s.id}" ${+studentId === +s.id ? "selected" : ""}>${esc(studentLabel(s))}</option>`).join("");
  const weekOpts = weeks.map(w => `<option value="${w.index}" ${startWeek === w.index ? "selected" : ""}>${w.label}</option>`).join("");

  const selectedDept = existing?.department || allowedDepts[0]?.id || "";
  // Der gerade bearbeitete Einsatz zählt nicht mit, sonst belegt er seinen eigenen Platz.
  const others = assignments.filter(a => !existing || +a.id !== +existing.id);
  /** Höchste Belegung in einer der Wochen [start, start + dur): dort entscheidet sich, ob der Platz reicht. */
  const countForPractice = (pId: number, dept: string, start: number, dur: number) => {
    const atStelle = others.filter(a => a.practiceId && +a.practiceId === pId && a.department === dept);
    let peak = 0;
    for (let w = start; w < start + Math.max(1, dur); w++) {
      const n = atStelle.filter(a => a.startWeek <= w && a.startWeek + a.durationWeeks > w).length;
      if (n > peak) peak = n;
    }
    return peak;
  };
  const countPeriodForPractice = (pId: number, dept: string) => {
    const seen = new Set<number>();
    others.forEach(a => {
      if (a.practiceId && +a.practiceId === pId && a.department === dept) seen.add(a.studentId);
    });
    return seen.size;
  };

  // Wünsche des Schülers laden (best-effort, kein Fehler wenn nicht verfügbar)
  let studentPrefs: Record<string, { practiceId: number; priority: number }[]> = {};
  try { studentPrefs = await api.schuelerWuensche(studentId); } catch { /* ignorieren */ }

  const buildPracOpts = (dept: string, start: number, dur: number, selectedPracId?: number) => {
    const prefs = studentPrefs[dept] || [];
    const prefMap = new Map(prefs.map(p => [+p.practiceId, p.priority]));
    const filtered = practices.filter(p => (p.capacities?.[dept] || 0) > 0);
    // Sortierung: Wünsche nach Priorität zuerst, dann Rest alphabetisch
    filtered.sort((a, b) => {
      const pa = prefMap.get(+a.id) ?? 9999;
      const pb = prefMap.get(+b.id) ?? 9999;
      if (pa !== pb) return pa - pb;
      return a.name.localeCompare(b.name);
    });
    return filtered.map(p => {
      const used = countForPractice(+p.id, dept, start, dur);
      const max = p.capacities?.[dept] || 0;
      const prio = prefMap.get(+p.id);
      const badge = prio ? `★${prio} ` : "";
      const ppMax = p.perPeriodCapacities?.[dept];
      const ppSuffix = (kapazitaetProZeitraumEnabled && ppMax)
        ? ` · Zeitraum: ${countPeriodForPractice(+p.id, dept)}/${ppMax}`
        : "";
      return `<option value="${p.id}" ${selectedPracId === +p.id ? "selected" : ""}>${badge}${p.name} (${used}/${max} belegt${ppSuffix})</option>`;
    }).join("");
  };

  const pracOpts = buildPracOpts(selectedDept, startWeek, existing?.durationWeeks || 1, +(existing?.practiceId ?? 0) || undefined);

  openModal(isEdit ? "Zuweisung bearbeiten" : "Neue Zuweisung", `
    ${!isEdit ? `<div class="assign-form-grid assign-form-type-row">
      <div class="assign-form-col">
        <div class="form-group">
          <label>Art</label>
          <select id="f-type"><option value="assign">Einsatz</option><option value="ferien">Ferien</option></select>
        </div>
      </div>
    </div>` : ""}
    <div id="f-assign-fields">
      <div class="assign-form-grid">
        <div class="assign-form-col">
          <div class="form-group"><label>Schüler</label><select id="f-stu">${stuOpts}</select></div>
          <div class="form-group"><label>Fachbereich</label><select id="f-dept">${deptOpts}</select></div>
          <label class="checkbox-label"><input type="checkbox" id="f-free" ${existing?.isFreeInput ? "checked" : ""} /> Freie Eingabe</label>
          <div class="form-group" id="f-prac-wrap"><label>Praktikumsstelle</label><select id="f-prac"><option value="">– Bitte wählen –</option>${pracOpts}</select></div>
          <div class="form-group hidden" id="f-freetext-wrap"><label>Freitext</label><input id="f-freetext" value="${esc(existing?.freeInputText || "")}" /></div>
          <div class="form-group"><label>Startwoche</label><select id="f-sw">${weekOpts}</select></div>
          <div class="assign-number-row">
            <div class="form-group"><label>Dauer (Wo.)</label><input type="number" id="f-dur" min="1" max="20" value="${existing?.durationWeeks || 1}" /></div>
            <div class="form-group"><label>Stunden pro Woche</label><input type="number" id="f-hpw" min="1" max="60" value="${existing?.hoursPerWeek ?? 40}" /></div>
          </div>
          ${vorplanungEnabled ? `
          <label class="checkbox-label"><input type="checkbox" id="f-visible" ${(existing ? existing.visibleToStudent !== false : vorplanungDefaultVisible) ? "checked" : ""} /> Für Schüler sichtbar</label>
          <label class="checkbox-label"><input type="checkbox" id="f-confirmed" ${(existing ? !existing.isPreliminary : !vorplanungDefaultPreliminary) ? "checked" : ""} /> Bestätigt</label>
          ` : ""}
          ${abrechnungEnabled ? `
          <label class="checkbox-label"><input type="checkbox" id="f-abgerechnet" ${existing?.abgerechnet ? "checked" : ""} /> Abgerechnet</label>
          ` : ""}
        </div>
        <div class="assign-form-col assign-form-col-comments">
          <div class="assign-hint hidden" id="f-prac-hint">
            <div class="assign-hint-title">${Icons.informationCircle} Hinweis zur Stelle <span class="label-hint">(nur Schule)</span></div>
            <div class="assign-hint-text" id="f-prac-hint-text"></div>
          </div>
          <div class="form-group">
            <label>Interner Kommentar <span class="label-hint">(nur Schule)</span></label>
            <textarea id="f-internal-note" rows="4" placeholder="Nur im Schul-Bereich sichtbar">${esc(existing?.internalNote || "")}</textarea>
          </div>
          <div class="form-group">
            <label>Kommentar für Schüler <span class="label-hint">(im Portal sichtbar)</span></label>
            <textarea id="f-student-note" rows="4" placeholder="Für den Schüler im Portal sichtbar">${esc(existing?.studentNote || "")}</textarea>
          </div>
        </div>
      </div>
    </div>
    ${!isEdit ? `<div id="f-vacation-fields" class="hidden">
      <div class="form-group"><label>Bezeichnung</label><input id="f-vname" placeholder="z.B. Herbstferien" /></div>
      <div class="form-grid">
        <div class="form-group"><label>Von (Woche)</label><select id="f-vsw">${weekOpts}</select></div>
        <div class="form-group"><label>Bis (Woche)</label><select id="f-vew">${weekOpts}</select></div>
      </div>
    </div>` : ""}
    <div class="alert alert-error hidden" id="f-modal-error"></div>
    <div class="form-actions"><button class="btn btn-secondary" id="f-cancel">${canWrite() ? "Abbrechen" : "Schließen"}</button>${writeOnly(`<button class="btn btn-primary" id="f-save">Speichern</button>`)}</div>`, { wide: true, stickyActions: true });

  const mb = modalBody();
  // Betrachter sehen den Einsatz schreibgeschützt
  lockInputs(mb);
  const freeCheck = mb.querySelector("#f-free") as HTMLInputElement;

  // Interner Kommentar der gewählten Stelle. Dort steht oft, für welche Schüler oder
  // Jahrgänge die Stelle passt, deshalb schreibgeschützt direkt im Formular.
  const updatePracHint = () => {
    const sel = mb.querySelector("#f-prac") as HTMLSelectElement;
    const p = practices.find(x => +x.id === +sel.value);
    const text = (!freeCheck.checked && p?.commentInternal || "").trim();
    (mb.querySelector("#f-prac-hint-text") as HTMLElement).textContent = text;
    mb.querySelector("#f-prac-hint")!.classList.toggle("hidden", !text);
  };

  const toggleFree = () => {
    mb.querySelector("#f-prac-wrap")!.classList.toggle("hidden", freeCheck.checked);
    mb.querySelector("#f-freetext-wrap")!.classList.toggle("hidden", !freeCheck.checked);
    updatePracHint();
  };
  freeCheck.addEventListener("change", toggleFree); toggleFree();

  // keepSelection: bei Startwoche oder Dauer bleibt die gewählte Stelle stehen, nur die
  // Belegung ändert sich. Ein anderer Fachbereich hat eine andere Stellenliste.
  const refreshPracList = (keepSelection = false) => {
    const dept = (mb.querySelector("#f-dept") as HTMLSelectElement).value;
    const currentWeek = +(mb.querySelector("#f-sw") as HTMLSelectElement).value;
    const dur = +(mb.querySelector("#f-dur") as HTMLInputElement).value || 1;
    const sel = mb.querySelector("#f-prac") as HTMLSelectElement;
    const selected = keepSelection ? +sel.value || undefined : undefined;
    sel.innerHTML = `<option value="">– Bitte wählen –</option>` + buildPracOpts(dept, currentWeek, dur, selected);
    updatePracHint();
  };

  mb.querySelector("#f-dept")!.addEventListener("change", () => refreshPracList());
  mb.querySelector("#f-sw")!.addEventListener("change", () => refreshPracList(true));
  mb.querySelector("#f-dur")!.addEventListener("input", () => refreshPracList(true));
  mb.querySelector("#f-prac")!.addEventListener("change", updatePracHint);

  // Beim Wechsel des Schülers: erlaubte Fachbereiche aktualisieren + Wünsche neu laden
  mb.querySelector("#f-stu")!.addEventListener("change", async () => {
    const newSid = +(mb.querySelector("#f-stu") as HTMLSelectElement).value;
    const newDepts = getDeptsForStudent(newSid);
    const deptSel = mb.querySelector("#f-dept") as HTMLSelectElement;
    deptSel.innerHTML = buildDeptOpts(newDepts);
    try { studentPrefs = await api.schuelerWuensche(newSid); } catch { studentPrefs = {}; }
    refreshPracList();
  });

  // Toggle Einsatz / Ferien (nur bei neuen Einträgen)
  if (!isEdit) {
    mb.querySelector("#f-type")!.addEventListener("change", () => {
      const ferien = (mb.querySelector("#f-type") as HTMLSelectElement).value === "ferien";
      mb.querySelector("#f-assign-fields")!.classList.toggle("hidden", ferien);
      mb.querySelector("#f-vacation-fields")!.classList.toggle("hidden", !ferien);
    });
    const vswSel = mb.querySelector("#f-vsw") as HTMLSelectElement;
    const vewSel = mb.querySelector("#f-vew") as HTMLSelectElement;
    vswSel.addEventListener("change", () => { if (+vewSel.value < +vswSel.value) vewSel.value = vswSel.value; });
  }

  mb.querySelector("#f-cancel")!.addEventListener("click", closeModal);
  mb.querySelector("#f-save")?.addEventListener("click", async () => {
    const entryType = !isEdit
      ? (mb.querySelector("#f-type") as HTMLSelectElement).value
      : "assign";

    if (entryType === "ferien") {
      const name = (mb.querySelector("#f-vname") as HTMLInputElement).value.trim();
      if (!name) return;
      const startWeek = +(mb.querySelector("#f-vsw") as HTMLSelectElement).value;
      const endWeek = +(mb.querySelector("#f-vew") as HTMLSelectElement).value;
      try {
        await api.vacations.create({
          planningPeriodId: activePeriod!.id, name, appliesToAll: false,
          studentId, startWeek, endWeek,
        });
      } catch (err: any) { showModalError(mb, `Speichern fehlgeschlagen: ${err.message}`); return; }
      closeModal();
      vacations = await api.vacations.list(activePeriod!.id);
      renderGridPreservingScroll(document.getElementById("main-content")!);
      return;
    }

    const data: any = {
      planningPeriodId: activePeriod!.id,
      studentId: +(mb.querySelector("#f-stu") as HTMLSelectElement).value,
      department: (mb.querySelector("#f-dept") as HTMLSelectElement).value,
      startWeek: +(mb.querySelector("#f-sw") as HTMLSelectElement).value,
      durationWeeks: +(mb.querySelector("#f-dur") as HTMLInputElement).value,
      // Leeres Feld ergaebe 0 und wuerde die Pflichtstunden-Bilanz verfaelschen. Die Spalte ist NOT NULL.
      hoursPerWeek: +(mb.querySelector("#f-hpw") as HTMLInputElement).value || (existing?.hoursPerWeek ?? 40),
      isFreeInput: freeCheck.checked,
      freeInputText: freeCheck.checked ? (mb.querySelector("#f-freetext") as HTMLInputElement).value : null,
      practiceId: freeCheck.checked ? null : +(mb.querySelector("#f-prac") as HTMLSelectElement).value || null,
      internalNote: (mb.querySelector("#f-internal-note") as HTMLTextAreaElement).value.trim() || null,
      studentNote: (mb.querySelector("#f-student-note") as HTMLTextAreaElement).value.trim() || null,
    };
    if (vorplanungEnabled) {
      data.visibleToStudent = (mb.querySelector("#f-visible") as HTMLInputElement).checked;
      data.isPreliminary = !(mb.querySelector("#f-confirmed") as HTMLInputElement).checked;
    }
    if (abrechnungEnabled) {
      data.abgerechnet = (mb.querySelector("#f-abgerechnet") as HTMLInputElement).checked;
    }
    const plan = await askVacationSplit(data, existing);
    if (plan === null) return; // Rückfrage abgebrochen, das Formular bleibt offen
    if (plan === "keep") {
      try {
        if (isEdit) await api.assignments.update(existing!.id, data); else await api.assignments.create(data);
      } catch (err: any) { showModalError(mb, `Speichern fehlgeschlagen: ${err.message}`); return; }
    } else {
      // Jeder Teil ist ein eigener Einsatz mit denselben Angaben. Beim Bearbeiten wird
      // der bestehende zum ersten Teil, die weiteren kommen neu dazu.
      let saved = 0;
      try {
        for (const part of plan) {
          const partData = { ...data, startWeek: part.startWeek, durationWeeks: part.durationWeeks };
          if (saved === 0 && isEdit) await api.assignments.update(existing!.id, partData);
          else await api.assignments.create(partData);
          saved++;
        }
      } catch (err: any) {
        if (saved === 0) { showModalError(mb, `Speichern fehlgeschlagen: ${err.message}`); return; }
        showToast(`Einsatz nur teilweise gespeichert (${saved} von ${plan.length} Teilen). Bitte prüfen.`);
      }
    }
    closeModal();
    assignments = await api.assignments.list(activePeriod!.id);
    renderGridPreservingScroll(document.getElementById("main-content")!);
  });
}

function openVacationForm() {
  if (!activePeriod) return;
  const stuOpts = filteredStudents().map(s => `<option value="${s.id}">${esc(studentLabel(s))}</option>`).join("");
  const weekOpts = weeks.map(w => `<option value="${w.index}">${w.label}</option>`).join("");
  openModal("Ferien hinzufügen", `
    <div class="form-group"><label>Bezeichnung</label><input id="f-vname" placeholder="z.B. Herbstferien" /></div>
    <label class="checkbox-label"><input type="checkbox" id="f-all-periods" /> Für alle Zeiträume</label>
    <div class="alert alert-info hidden" id="f-all-periods-hint" style="margin-top:.5rem">Die Ferien gelten für alle Schüler und erscheinen in jedem Zeitraum, der diese Wochen enthält, auch in später angelegten. Bearbeiten und Löschen ist aus jedem dieser Zeiträume möglich.</div>
    <div id="f-scope-wrap">
      <label class="checkbox-label"><input type="checkbox" id="f-all" checked /> Für alle Schüler</label>
      <div class="form-group hidden" id="f-vstu-wrap"><label>Schüler</label><select id="f-vstu"><option value="">– Bitte wählen –</option>${stuOpts}</select></div>
    </div>
    <div class="form-grid form-grid-stacked">
      <div class="form-group"><label>Von (Woche)</label><select id="f-vsw">${weekOpts}</select></div>
      <div class="form-group"><label>Bis (Woche)</label><select id="f-vew">${weekOpts}</select></div>
    </div>
    <div class="alert alert-error hidden" id="f-modal-error"></div>
    <div class="form-actions"><button class="btn btn-secondary" id="f-cancel">Abbrechen</button><button class="btn btn-amber" id="f-save">Speichern</button></div>`);
  const mb = modalBody();
  const allCheck = mb.querySelector("#f-all") as HTMLInputElement;
  allCheck.addEventListener("change", () => mb.querySelector("#f-vstu-wrap")!.classList.toggle("hidden", allCheck.checked));
  // Ferien für alle Zeiträume gelten immer für alle Schüler, eine Schülerauswahl gibt es dann nicht
  const allPeriodsCheck = mb.querySelector("#f-all-periods") as HTMLInputElement;
  allPeriodsCheck.addEventListener("change", () => {
    mb.querySelector("#f-scope-wrap")!.classList.toggle("hidden", allPeriodsCheck.checked);
    mb.querySelector("#f-all-periods-hint")!.classList.toggle("hidden", !allPeriodsCheck.checked);
  });
  const swSel = mb.querySelector("#f-vsw") as HTMLSelectElement;
  const ewSel = mb.querySelector("#f-vew") as HTMLSelectElement;
  swSel.addEventListener("change", () => { ewSel.value = swSel.value; });
  mb.querySelector("#f-cancel")!.addEventListener("click", closeModal);
  mb.querySelector("#f-save")!.addEventListener("click", async () => {
    const name = (mb.querySelector("#f-vname") as HTMLInputElement).value.trim();
    if (!name) return;
    const startWeek = +(mb.querySelector("#f-vsw") as HTMLSelectElement).value;
    const endWeek = +(mb.querySelector("#f-vew") as HTMLSelectElement).value;

    if (allPeriodsCheck.checked) {
      if (endWeek < startWeek) { showModalError(mb, "Die Bis-Woche liegt vor der Von-Woche."); return; }
      const sunday = mondayAt(endWeek); sunday.setDate(sunday.getDate() + 6);
      try {
        await api.sharedVacations.create({ name, startDate: isoDate(mondayAt(startWeek)), endDate: isoDate(sunday) });
      } catch (err: any) { showModalError(mb, `Speichern fehlgeschlagen: ${err.message}`); return; }
    } else if (allCheck.checked) {
      // "Alle Schüler" meint die aktuell gefilterten, nicht zwingend den ganzen Jahrgang.
      // Die Schülersuche zählt nicht mit, sonst bekäme nur der gesuchte Schüler Ferien.
      // Jeder Schüler bekommt einen eigenen Eintrag; scheitern nur einzelne, sind die
      // übrigen bereits gespeichert und das Fenster kann zu.
      const shown = filteredStudents();
      const results = await Promise.allSettled(shown.map(s =>
        api.vacations.create({
          planningPeriodId: activePeriod!.id, name, appliesToAll: false,
          studentId: s.id, startWeek, endWeek,
        })
      ));
      const failed = results.filter((r): r is PromiseRejectedResult => r.status === "rejected");
      if (failed.length > 0 && failed.length === results.length) {
        showModalError(mb, `Speichern fehlgeschlagen: ${failed[0].reason?.message ?? failed[0].reason}`);
        return;
      }
      if (failed.length > 0) {
        showToast(`Ferien nur für ${results.length - failed.length} von ${results.length} Schülern eingetragen: ${failed[0].reason?.message ?? failed[0].reason}`);
      }
    } else {
      const studentId = +(mb.querySelector("#f-vstu") as HTMLSelectElement).value || null;
      try {
        await api.vacations.create({
          planningPeriodId: activePeriod!.id, name, appliesToAll: false,
          studentId, startWeek, endWeek,
        });
      } catch (err: any) { showModalError(mb, `Speichern fehlgeschlagen: ${err.message}`); return; }
    }
    closeModal();
    vacations = await api.vacations.list(activePeriod!.id);
    renderGridPreservingScroll(document.getElementById("main-content")!);
  });
}

function openEditVacationForm(vac: Vacation) {
  // Ferien für alle Zeiträume können über den geladenen Zeitraum hinausreichen. Die Auswahl
  // umfasst dann auch diese Wochen, sonst würde Speichern sie in allen Zeiträumen kürzen.
  let fromIdx = 0, toIdx = weeks.length - 1, selStart = vac.startWeek, selEnd = vac.endWeek;
  if (vac.shared) {
    const periodMonday = mondayAt(0);
    selStart = weekDiff(periodMonday, parseLocal(vac.shared.startDate));
    selEnd = weekDiff(periodMonday, parseLocal(vac.shared.endDate));
    fromIdx = Math.min(fromIdx, selStart);
    toIdx = Math.max(toIdx, selEnd);
  }
  const opts = (selected: number) => {
    let html = "";
    for (let i = fromIdx; i <= toIdx; i++) {
      html += `<option value="${i}" ${i === selected ? "selected" : ""}>${weekLabelAt(i)}</option>`;
    }
    return html;
  };
  const used = vac.shared?.periods ?? [];
  const sharedHint = vac.shared
    ? `<div class="alert alert-info">Diese Ferien gelten für alle Zeiträume. ${used.length > 1
        ? `Änderungen wirken sich auf alle ${used.length} Zeiträume aus, in denen sie verwendet werden: ${used.map(p => esc(p.name)).join(", ")}.`
        : "Änderungen gelten auch für später angelegte Zeiträume."}</div>`
    : "";
  openModal("Ferien bearbeiten", `
    ${sharedHint}
    <div class="form-group"><label>Bezeichnung</label><input id="f-vname" value="${esc(vac.name)}" /></div>
    <div class="form-grid form-grid-stacked">
      <div class="form-group"><label>Von (Woche)</label><select id="f-vsw">${opts(selStart)}</select></div>
      <div class="form-group"><label>Bis (Woche)</label><select id="f-vew">${opts(selEnd)}</select></div>
    </div>
    <div class="alert alert-error hidden" id="f-modal-error"></div>
    <div class="form-actions"><button class="btn btn-secondary" id="f-cancel">${canWrite() ? "Abbrechen" : "Schließen"}</button>${writeOnly(`<button class="btn btn-amber" id="f-save">Speichern</button>`)}</div>`);
  const mb = modalBody();
  // Betrachter sehen die Ferien schreibgeschützt
  lockInputs(mb);
  const swSelEdit = mb.querySelector("#f-vsw") as HTMLSelectElement;
  const ewSelEdit = mb.querySelector("#f-vew") as HTMLSelectElement;
  swSelEdit.addEventListener("change", () => { ewSelEdit.value = swSelEdit.value; });
  mb.querySelector("#f-cancel")!.addEventListener("click", closeModal);
  mb.querySelector("#f-save")?.addEventListener("click", async () => {
    const name = (mb.querySelector("#f-vname") as HTMLInputElement).value.trim();
    if (!name) return;
    const startWeek = +swSelEdit.value;
    const endWeek = +ewSelEdit.value;
    if (vac.shared) {
      if (endWeek < startWeek) { showModalError(mb, "Die Bis-Woche liegt vor der Von-Woche."); return; }
      const sunday = mondayAt(endWeek); sunday.setDate(sunday.getDate() + 6);
      try {
        await api.sharedVacations.update(vac.id, { name, startDate: isoDate(mondayAt(startWeek)), endDate: isoDate(sunday) });
      } catch (err: any) { showModalError(mb, `Speichern fehlgeschlagen: ${err.message}`); return; }
    } else {
      await api.vacations.update(vac.id, {
        name, startWeek, endWeek,
        studentId: vac.studentId, planningPeriodId: vac.planningPeriodId, appliesToAll: vac.appliesToAll,
      });
    }
    closeModal();
    vacations = await api.vacations.list(activePeriod!.id);
    renderGridPreservingScroll(document.getElementById("main-content")!);
  });
}

/** Markup des Standard-Klassenfilters im Zeitraum-Modal. */
function periodClassPickerHtml(): string {
  const items = classes.length
    ? classes.map(c => `<label class="dropdown-check-item"><input type="checkbox" value="${c.id}" /> ${esc(c.name)}</label>`).join("")
    : `<div class="dropdown-check-item text-muted">Keine Klassen vorhanden</div>`;
  return `
    <div class="form-group">
      <label>Standard-Klassenfilter</label>
      <div class="dropdown-check" id="f-pclasses">
        <button type="button" class="dropdown-check-btn">Alle Klassen</button>
        <div class="dropdown-check-panel dropdown-check-panel-left">${items}</div>
      </div>
      <div class="form-hint">Beim Wechsel zu diesem Zeitraum filtert die Planung automatisch auf diese Klassen. Ohne Auswahl werden alle Klassen angezeigt.</div>
    </div>`;
}

/** Checkbox "Für Schüler sichtbar" im Zeitraum-Modal. */
function periodVisibleHtml(checked: boolean): string {
  return `
    <div class="form-group">
      <label class="checkbox-label"><input type="checkbox" id="f-pvisible" ${checked ? "checked" : ""} /> Für Schüler sichtbar</label>
      <div class="form-hint">Ohne Haken sehen Schüler und Praktikumsstellen diesen Zeitraum nicht, und Schüler bekommen dazu keine Mails. Praktisch für Testplanungen.</div>
    </div>`;
}

/** Verdrahtet den Standard-Klassenfilter und liefert eine Funktion für die aktuelle Auswahl. */
function initPeriodClassPicker(mb: HTMLElement, selected: number[]): () => number[] {
  const drop = mb.querySelector("#f-pclasses") as HTMLElement;
  let ids: number[] = [];
  const handle = initDropdownCheck(drop, (vals) => { ids = vals.map(Number); }, { allSelectedShowsDefault: true });
  handle.setValues(selected.map(String));
  // Auswahl aus dem Dropdown übernehmen, damit zwischenzeitlich gelöschte Klassen wegfallen
  ids = handle.getValues().map(Number);
  const ac = new AbortController();
  document.addEventListener("click", () => drop.classList.remove("open"), { signal: ac.signal });
  onModalClose(() => ac.abort());
  // Sind alle Klassen gewählt, wird nichts gespeichert: der Button zeigt dann "Alle
  // Klassen", und genau das soll auch für später angelegte Klassen gelten.
  return () => (ids.length === classes.length ? [] : ids);
}

function openPeriodForm() {
  openModal("Neuer Planungszeitraum", `
    <div class="form-group"><label>Bezeichnung</label><input id="f-pname" placeholder="z.B. Sommersemester 2025" /></div>
    <div class="form-group"><label>Zeitraum wählen</label><div id="f-week-picker"></div></div>
    ${periodClassPickerHtml()}
    ${periodVisibleHtml(true)}
    <div class="alert alert-error hidden" id="f-modal-error"></div>
    <div class="form-actions"><button class="btn btn-secondary" id="f-cancel">Abbrechen</button><button class="btn btn-primary" id="f-save">Anlegen</button></div>`);
  const mb = modalBody();
  let startDate = "", endDate = "";
  renderWeekRangePicker(mb.querySelector("#f-week-picker")!, undefined, (s, e) => { startDate = s; endDate = e; });
  const getDefaultClassIds = initPeriodClassPicker(mb, []);
  mb.querySelector("#f-cancel")!.addEventListener("click", closeModal);
  mb.querySelector("#f-save")!.addEventListener("click", async () => {
    const name = (mb.querySelector("#f-pname") as HTMLInputElement).value.trim();
    if (!name || !startDate || !endDate) { showModalError(mb, "Bitte Bezeichnung und Zeitraum angeben."); return; }
    let created: PlanningPeriod;
    try {
      created = await api.periods.create({
        name, startDate, endDate, defaultClassIds: getDefaultClassIds(),
        visibleToStudents: (mb.querySelector("#f-pvisible") as HTMLInputElement).checked,
      });
    } catch (err: any) { showModalError(mb, `Anlegen fehlgeschlagen: ${err.message}`); return; }
    closeModal();
    dropPreviewAfterPeriodChange();
    periods = await api.periods.list();
    activePeriod = periods.find(p => +p.id === +created.id) || periods[0];
    if (activePeriod) await loadPeriodData(activePeriod);
    const mainEl = document.getElementById("main-content")!;
    fillPeriodDropdown(mainEl);
    applyPeriodClassDefault(activePeriod);
    renderGrid(mainEl);
  });
}

function openEditPeriodForm(el: HTMLElement) {
  if (!activePeriod) return;
  const p = activePeriod;
  openModal("Zeitraum bearbeiten", `
    <div class="form-group"><label>Bezeichnung</label><input id="f-pname" value="${esc(p.name)}" /></div>
    <div class="form-group"><label>Zeitraum wählen</label><div id="f-week-picker"></div></div>
    ${periodClassPickerHtml()}
    ${periodVisibleHtml(p.visibleToStudents !== false)}
    <div class="alert alert-error hidden" id="f-modal-error"></div>
    <div class="form-actions"><button class="btn btn-secondary" id="f-cancel">Abbrechen</button><button class="btn btn-primary" id="f-save">Speichern</button></div>`);
  const mb = modalBody();
  let startDate = p.startDate, endDate = p.endDate;
  // Datenverlust wird im Fenster selbst bestätigt: der Hinweis erscheint inline und
  // der Knopf wird rot. Erst der zweite Klick speichert.
  let datenverlustBestaetigt = false;
  const saveBtn = mb.querySelector("#f-save") as HTMLButtonElement;
  const resetDatenverlustHinweis = () => {
    datenverlustBestaetigt = false;
    hideModalError(mb);
    saveBtn.textContent = "Speichern";
    saveBtn.classList.replace("btn-danger-fill", "btn-primary");
  };
  // Ein neu gewählter Zeitraum verliert andere Daten als der zuvor bestätigte,
  // deshalb hier zurück auf Anfang.
  renderWeekRangePicker(mb.querySelector("#f-week-picker")!, { startDate: p.startDate, endDate: p.endDate }, (s, e) => {
    startDate = s; endDate = e;
    resetDatenverlustHinweis();
  });
  const getDefaultClassIds = initPeriodClassPicker(mb, p.defaultClassIds || []);
  mb.querySelector("#f-cancel")!.addEventListener("click", closeModal);
  saveBtn.addEventListener("click", async () => {
    const name = (mb.querySelector("#f-pname") as HTMLInputElement).value.trim();
    if (!name || !startDate || !endDate) { showModalError(mb, "Bitte Bezeichnung und Zeitraum angeben."); return; }
    // Prüfen ob Assignments/Ferien nach der Verschiebung verloren gehen.
    // startWeek ist ein 0-basierter Index in weeks[], deshalb wird hier in echten
    // Wochenabständen gerechnet und nicht mit KW-Nummern (die springen am Jahreswechsel).
    const newStart = parseLocal(startDate), newEnd = parseLocal(endDate);
    const offset = weekDiff(parseLocal(p.startDate), newStart);
    const newMaxWeek = weekDiff(newStart, newEnd);
    const lostA = assignments.filter(a => {
      const shifted = a.startWeek - offset;
      return shifted < 0 || shifted > newMaxWeek || shifted + a.durationWeeks - 1 > newMaxWeek;
    });
    // Ferien für alle Zeiträume hängen an Daten, nicht an Wochenindizes: sie gehen nicht verloren.
    const lostV = vacations.filter(v => !v.shared).filter(v => {
      const sShifted = v.startWeek - offset, eShifted = v.endWeek - offset;
      return sShifted < 0 || eShifted < 0 || sShifted > newMaxWeek || eShifted > newMaxWeek;
    });
    if ((lostA.length > 0 || lostV.length > 0) && !datenverlustBestaetigt) {
      datenverlustBestaetigt = true;
      showModalError(mb, `${lostA.length} Zuweisung(en) und ${lostV.length} Ferien liegen außerhalb des neuen Zeitraums und werden gelöscht.`);
      saveBtn.textContent = "Trotzdem speichern";
      saveBtn.classList.replace("btn-primary", "btn-danger-fill");
      return;
    }
    try {
      await api.periods.update(+p.id, {
        name, startDate, endDate, defaultClassIds: getDefaultClassIds(),
        visibleToStudents: (mb.querySelector("#f-pvisible") as HTMLInputElement).checked,
      });
    } catch (err: any) { showModalError(mb, `Speichern fehlgeschlagen: ${err.message}`); return; }
    closeModal();
    dropPreviewAfterPeriodChange();
    periods = await api.periods.list();
    activePeriod = periods.find(x => +x.id === +p.id) || periods[0] || null;
    if (activePeriod) await loadPeriodData(activePeriod);
    else { assignments = []; vacations = []; weeks = []; }
    fillPeriodDropdown(el);
    applyPeriodClassDefault(activePeriod);
    renderGrid(el);
  });
}

// Das Fenster ist selbst die Rückfrage: Titel, Warnhinweis und roter Knopf sagen
// deutlich genug, was passiert. Eine zweite Abfrage wäre aus einem offenen Modal
// heraus ohnehin nicht darstellbar, modal.ts hat nur einen Slot.
function openDeletePeriodForm(el: HTMLElement) {
  if (periods.length === 0) { showToast("Keine Zeiträume vorhanden."); return; }
  const opts = periods.map(p => `<option value="${p.id}">${esc(p.name)}</option>`).join("");
  openModal("Zeitraum löschen", `
    <div class="form-group"><label>Zeitraum auswählen</label><select id="f-del-period">${opts}</select></div>
    <div class="alert alert-error">Alle zugehörigen Zuweisungen und Ferien werden ebenfalls gelöscht. Schüler mit laufenden oder künftigen sichtbaren Einsätzen werden per Mail informiert, sofern die Benachrichtigung aktiv ist.</div>
    <div class="alert alert-error hidden" id="f-modal-error"></div>
    <div class="form-actions"><button class="btn btn-secondary" id="f-cancel">Abbrechen</button><button class="btn btn-danger-fill" id="f-confirm-del">Löschen</button></div>`);
  const mb = modalBody();
  mb.querySelector("#f-cancel")!.addEventListener("click", closeModal);
  mb.querySelector("#f-confirm-del")!.addEventListener("click", async () => {
    const id = +(mb.querySelector("#f-del-period") as HTMLSelectElement).value;
    try {
      await api.periods.delete(id);
    } catch (err: any) { showModalError(mb, `Löschen fehlgeschlagen: ${err.message}`); return; }
    closeModal();
    if (previewPeriodId === id) dropPreviewAfterPeriodChange();
    periods = await api.periods.list();
    if (activePeriod && +activePeriod.id === id) {
      activePeriod = periods[0] || null;
      if (activePeriod) await loadPeriodData(activePeriod);
      else { assignments = []; vacations = []; weeks = []; }
      applyPeriodClassDefault(activePeriod);
    }
    fillPeriodDropdown(el);
    renderGrid(el);
  });
}

function openDeleteVacationForm(el: HTMLElement) {
  if (!activePeriod || vacations.length === 0) { showToast("Keine Ferien im aktuellen Zeitraum vorhanden."); return; }
  // Ferien des Zeitraums hängen je an einem Schüler und werden über den Namen gebündelt.
  // Ferien für alle Zeiträume sind je ein eigener Eintrag mit eigener Rückfrage.
  const names = [...new Set(vacations.filter(v => !v.shared).map(v => v.name))];
  const shared = vacations.filter(v => v.shared);
  const entries: ({ kind: "name"; name: string } | { kind: "shared"; vac: Vacation })[] = [
    ...names.map(name => ({ kind: "name" as const, name })),
    ...shared.map(vac => ({ kind: "shared" as const, vac })),
  ];
  const opts = entries.map((e, i) => `<option value="${i}">${e.kind === "name" ? esc(e.name) : `${esc(e.vac.name)} (alle Zeiträume)`}</option>`).join("");
  openModal("Ferien löschen", `
    <div class="form-group"><label>Ferien auswählen</label><select id="f-del-vac">${opts}</select></div>
    <div class="alert alert-error" id="f-del-vac-hint"></div>
    <div class="alert alert-error hidden" id="f-modal-error"></div>
    <div class="form-actions"><button class="btn btn-secondary" id="f-cancel">Abbrechen</button><button class="btn btn-danger-fill" id="f-confirm-del">Löschen</button></div>`);
  const mb = modalBody();
  const sel = mb.querySelector("#f-del-vac") as HTMLSelectElement;
  const hint = mb.querySelector("#f-del-vac-hint") as HTMLElement;
  const updateHint = () => {
    hint.textContent = entries[+sel.value]?.kind === "shared"
      ? "Diese Ferien gelten für alle Zeiträume. Im nächsten Schritt wählst du, ob sie nur aus diesem Zeitraum oder in allen Zeiträumen gelöscht werden."
      : "Die Ferien werden bei allen Schülern gelöscht, bei denen sie eingetragen sind.";
  };
  sel.addEventListener("change", updateHint); updateHint();
  mb.querySelector("#f-cancel")!.addEventListener("click", closeModal);
  mb.querySelector("#f-confirm-del")!.addEventListener("click", async () => {
    const entry = entries[+sel.value];
    if (!entry) return;
    if (entry.kind === "shared") {
      // Die Rückfrage legt sich über dieses Fenster; bei Abbruch bleibt es stehen
      if (!(await deleteSharedVacation(entry.vac))) return;
      closeModal();
      renderGrid(el);
      return;
    }
    const toDelete = vacations.filter(v => !v.shared && v.name === entry.name);
    try {
      await Promise.all(toDelete.map(v => api.vacations.delete(v.id)));
    } catch (err: any) {
      // Teilerfolg ist möglich: die Ferien hängen an je einem Schüler und werden
      // parallel gelöscht. Deshalb neu laden, damit das Grid den Ist-Stand zeigt.
      vacations = await api.vacations.list(activePeriod!.id).catch(() => vacations);
      renderGrid(el);
      showModalError(mb, `Löschen fehlgeschlagen: ${err.message}`);
      return;
    }
    closeModal();
    vacations = await api.vacations.list(activePeriod!.id);
    renderGrid(el);
  });
}

function openAutoPlanModal(el: HTMLElement) {
  if (!activePeriod) { showToast("Kein Zeitraum ausgewählt."); return; }

  // Nur Fachbereiche anzeigen, die in mindestens einer aktiven Gruppe sind
  const activeGroupDeptIds = new Set(departmentGroups.flatMap(g => g.departments.map(d => d.id)));
  const modalDepts = departments.filter(d => activeGroupDeptIds.has(d.id));

  // Trackt vom Nutzer abgewählte Fachbereich-IDs über Rebuilds hinweg
  const uncheckedDeptIds = new Set<string>();

  const buildDeptListHtml = (depts: typeof departments, searchQuery = "") => {
    const query = searchQuery.toLowerCase().trim();
    const filtered = query ? depts.filter(d => d.nameDe.toLowerCase().includes(query)) : depts;
    if (!filtered.length) {
      return `<div class="map-list-item"><em class="text-muted">${query ? "Keine Treffer" : "Keine Fachbereiche verfügbar"}</em></div>`;
    }
    let html = "";
    const renderedIds = new Set<string>();
    for (const g of departmentGroups) {
      const gd = filtered.filter(d => g.departments.some(x => x.id === d.id));
      if (!gd.length) continue;
      html += `<div class="map-list-separator">${esc(g.name)}</div>`;
      for (const d of gd) {
        renderedIds.add(d.id);
        html += `<div class="map-list-item"><label class="checkbox-label"><input type="checkbox" value="${d.id}" checked />${esc(d.nameDe)}</label></div>`;
      }
    }
    const ungrouped = filtered.filter(d => !renderedIds.has(d.id));
    if (ungrouped.length) {
      html += `<div class="map-list-separator">Weitere Fachbereiche</div>`;
      for (const d of ungrouped) {
        html += `<div class="map-list-item"><label class="checkbox-label"><input type="checkbox" value="${d.id}" checked />${esc(d.nameDe)}</label></div>`;
      }
    }
    return html;
  };

  const classCheckboxesHtml = classes.length
    ? classes.map(c => `<label class="checkbox-label"><input type="checkbox" value="${c.id}" checked />${esc(c.name)}</label>`).join("")
    : `<em class="text-muted">Keine Klassen</em>`;

  openModal("Automatische Planung", `
    <small class="text-muted">Klassen, Fachbereiche und Einstellungen wählen</small>
    <div class="ap-cols">
      <div class="ap-col">
        <div class="map-list-separator">Klassen</div>
        <div class="ap-stack" id="f-ap-classes">${classCheckboxesHtml}</div>
      </div>
      <div class="ap-col">
        <div class="map-list-separator ap-row">
          <span>Fachbereiche (<span id="f-ap-dept-count">0</span>)</span>
          <button class="btn btn-sm btn-secondary" id="f-ap-toggle-all">Alle abwählen</button>
        </div>
        <div class="api-search-bar">
          <input type="search" id="f-ap-search" placeholder="Fachbereiche suchen…" />
        </div>
        <div id="f-ap-depts"></div>
      </div>
    </div>
    <div class="map-list-separator">Einstellungen</div>
    <div class="ap-stack">
      <label class="checkbox-label"><input type="checkbox" id="f-ap-keep" checked /> Bestehende Zuweisungen beibehalten</label>
      <label class="checkbox-label"><input type="checkbox" id="f-ap-distance" checked /> Wohnortnähe berücksichtigen</label>
    </div>
    <div class="ap-footer">
      <span class="text-muted" id="f-ap-status"></span>
      <div class="ap-btn-group">
        <button class="btn btn-secondary" id="f-cancel">Abbrechen</button>
        <button class="btn btn-primary" id="f-ap-run">Planung berechnen</button>
      </div>
    </div>`, { wide: true, fixedSize: true });

  const mb = modalBody();

  const updateDeptCount = () => {
    const count = mb.querySelectorAll("#f-ap-depts input:checked").length;
    const el2 = mb.querySelector("#f-ap-dept-count");
    if (el2) el2.textContent = String(count);
  };

  const updateToggleLabel = () => {
    const cbs = Array.from(mb.querySelectorAll("#f-ap-depts input[type=checkbox]")) as HTMLInputElement[];
    const allChecked = cbs.length > 0 && cbs.every(cb => cb.checked);
    const btn = mb.querySelector("#f-ap-toggle-all");
    if (btn) btn.textContent = allChecked ? "Alle abwählen" : "Alle auswählen";
  };

  const updateStatus = () => {
    const classCount = mb.querySelectorAll("#f-ap-classes input:checked").length;
    const deptCount = mb.querySelectorAll("#f-ap-depts input:checked").length;
    const s = mb.querySelector("#f-ap-status");
    if (s) s.textContent = `${classCount} ${classCount === 1 ? "Klasse" : "Klassen"} · ${deptCount} ${deptCount === 1 ? "Fachbereich" : "Fachbereiche"} ausgewählt`;
  };

  const rebuildDeptList = (searchQuery = "") => {
    const selectedClassIds = Array.from(mb.querySelectorAll("#f-ap-classes input:checked")).map((i: any) => +i.value);
    const selectedClasses = classes.filter(c => selectedClassIds.includes(c.id));
    let relevantDepts = modalDepts;
    if (selectedClasses.length > 0) {
      const addedIds = new Set<string>();
      relevantDepts = [];
      for (const c of selectedClasses) {
        let allowed: Set<string> | undefined;
        if (c.departmentGroupId) allowed = groupDeptIds.get(c.departmentGroupId);
        else if (c.customGroupId) allowed = customGroupDeptIds.get(c.customGroupId);
        for (const d of modalDepts) {
          if (!addedIds.has(d.id) && (!allowed || allowed.has(d.id))) {
            relevantDepts.push(d); addedIds.add(d.id);
          }
        }
      }
    }
    mb.querySelector("#f-ap-depts")!.innerHTML = buildDeptListHtml(relevantDepts, searchQuery);
    // Abgewählte Einträge wiederherstellen
    mb.querySelectorAll("#f-ap-depts input[type=checkbox]").forEach((cb: any) => {
      if (uncheckedDeptIds.has(cb.value)) cb.checked = false;
    });
    updateDeptCount();
    updateToggleLabel();
    updateStatus();
  };

  // Initialer Aufbau
  rebuildDeptList();

  // Klassen: Fachbereich-Liste reaktiv neu aufbauen
  mb.querySelector("#f-ap-classes")!.addEventListener("change", (e) => {
    const cb = e.target as HTMLInputElement;
    if (cb.type !== "checkbox") return;
    rebuildDeptList((mb.querySelector("#f-ap-search") as HTMLInputElement).value);
  });

  // Fachbereich-Checkboxen
  mb.querySelector("#f-ap-depts")!.addEventListener("change", (e) => {
    const cb = e.target as HTMLInputElement;
    if (cb.type !== "checkbox") return;
    if (cb.checked) uncheckedDeptIds.delete(cb.value);
    else uncheckedDeptIds.add(cb.value);
    updateDeptCount();
    updateToggleLabel();
    updateStatus();
  });

  // Suche
  mb.querySelector("#f-ap-search")!.addEventListener("input", (e) => {
    rebuildDeptList((e.target as HTMLInputElement).value);
  });

  // Alle / Keine auswählen
  mb.querySelector("#f-ap-toggle-all")!.addEventListener("click", () => {
    const cbs = Array.from(mb.querySelectorAll("#f-ap-depts input[type=checkbox]")) as HTMLInputElement[];
    const allChecked = cbs.every(cb => cb.checked);
    cbs.forEach(cb => {
      cb.checked = !allChecked;
      if (!allChecked) uncheckedDeptIds.delete(cb.value);
      else uncheckedDeptIds.add(cb.value);
    });
    updateDeptCount();
    updateToggleLabel();
    updateStatus();
  });

  mb.querySelector("#f-cancel")!.addEventListener("click", closeModal);

  mb.querySelector("#f-ap-run")!.addEventListener("click", async () => {
    const classIds = Array.from(mb.querySelectorAll("#f-ap-classes input:checked")).map((i: any) => +i.value);
    const departmentIds = Array.from(mb.querySelectorAll("#f-ap-depts input:checked")).map((i: any) => i.value);
    const keepExisting = (mb.querySelector("#f-ap-keep") as HTMLInputElement).checked;
    const useDistance = (mb.querySelector("#f-ap-distance") as HTMLInputElement).checked;
    const config = { classIds, departmentIds, hoursPerWeek: 40, keepExisting, useDistance };
    mb.innerHTML = `<div style="display:flex;flex-direction:column;align-items:center;gap:1rem;padding:2rem"><div class="spinner"></div><p>Berechne optimale Planung...</p></div>`;
    try {
      const periodId = +activePeriod!.id;
      const result = await api.autoPlanning.run(periodId, config);
      closeModal();
      previewPeriodId = periodId;
      previewReplacedIds = new Set((result.replacedAssignmentIds ?? []).map(Number));
      previewAssignments = result.proposed;
      previewStats = result.stats;
      previewWarnings = result.warnings;
      previewConfig = config;
      renderGrid(el);
    } catch (err: any) {
      mb.innerHTML = `<div class="alert alert-error">Fehler: ${esc(err.message)}</div><div class="form-actions"><button class="btn btn-secondary" id="f-close">Schließen</button></div>`;
      mb.querySelector("#f-close")!.addEventListener("click", closeModal);
    }
  });
}

export async function render(el: HTMLElement) {
  (el as any).__renderAC?.abort();
  const ac = new AbortController();
  (el as any).__renderAC = ac;
  filterStudentId = null; studentSearch = null;
  ansicht = loadAnsicht();

  el.innerHTML = `
    <div style="display:flex;flex-direction:column;height:calc(100vh - 80px)">
      <div class="page-header"><h1>Planung</h1>
        <div style="display:flex;gap:.5rem">
          <button class="btn btn-secondary btn-square" id="btn-progress" title="Zum Praktikumsfortschritt der Schüler" aria-label="Zum Praktikumsfortschritt der Schüler">${Icons.chartBar}</button>
          ${writeOnly(`<button class="btn btn-primary" id="btn-auto-plan">Automatische Planung</button>
          <div class="dropdown-check" id="dd-manage">
            <button class="btn btn-secondary dropdown-check-btn">Verwalten</button>
            <div class="dropdown-check-panel">
              <div class="dropdown-check-item" id="add-vacation">Ferien eintragen</div>
              <div class="dropdown-check-item" id="add-period">Zeitraum erstellen</div>
              <div class="dropdown-divider"></div>
              <div class="dropdown-check-item" id="edit-period">Zeitraum bearbeiten</div>
              <div class="dropdown-check-item" id="del-period">Zeitraum löschen</div>
              <div class="dropdown-check-item" id="del-vacation">Ferien löschen</div>
              <div class="dropdown-divider hidden" id="vorplanung-divider"></div>
              <div class="dropdown-check-item hidden" id="make-all-visible">Alle sichtbar machen</div>
              <div class="dropdown-check-item hidden" id="confirm-all">Alle als bestätigt markieren</div>
            </div>
          </div>`)}
          <div class="dropdown-check" id="dd-ansicht">
            <button class="btn btn-secondary dropdown-check-btn">Ansicht</button>
            <div class="dropdown-check-panel dropdown-check-panel-tall">
              <div class="dropdown-check-group">Spaltenbreite</div>
              <div class="dropdown-check-item" data-breite="normal">Normal</div>
              <div class="dropdown-check-item" data-breite="schmal">Schmal</div>
              <div class="dropdown-check-item" data-breite="kompakt">Kompakt, Namen gedreht</div>
              <div class="dropdown-divider"></div>
              <div class="dropdown-check-group">Namen</div>
              <div class="dropdown-check-item" data-namen="nachname">Nachname, Vorname</div>
              <div class="dropdown-check-item" data-namen="vorname">Vorname Nachname</div>
            </div>
          </div>
          <div class="dropdown-check" id="dd-download">
            <button class="btn btn-secondary dropdown-check-btn">Download</button>
            <div class="dropdown-check-panel">
              <div class="dropdown-check-item" id="export-gesamt-xlsx">Gesamtansicht als Excel</div>
              <div class="dropdown-check-item" id="export-gesamt-pdf">Gesamtansicht als PDF</div>
              <div class="dropdown-divider"></div>
              <div class="dropdown-check-item" id="export-schueler-xlsx">Nach Schülern als Excel</div>
              <div class="dropdown-check-item" id="export-schueler-pdf">Nach Schülern als PDF</div>
              <div class="dropdown-divider"></div>
              <div class="dropdown-check-item" id="export-prakt-xlsx">Nach Prakt.stellen als Excel</div>
              <div class="dropdown-check-item" id="export-prakt-pdf">Nach Prakt.stellen als PDF</div>
              <div class="dropdown-divider hidden" id="export-kv-divider"></div>
              <div class="dropdown-check-item hidden" id="export-kv-xlsx">Nach Kreisverband als Excel</div>
              <div class="dropdown-check-item hidden" id="export-kv-pdf">Nach Kreisverband als PDF</div>
            </div>
          </div>
        </div>
      </div>
      <div class="filter-bar" style="display:flex;justify-content:space-between;align-items:center">
        <div class="planning-filters">
          <div class="dropdown-check" id="filter-period">
            <button type="button" class="dropdown-check-btn">Kein Zeitraum</button>
            <div class="dropdown-check-panel dropdown-check-panel-left"></div>
          </div>
          <div class="dropdown-check" id="filter-class">
            <button type="button" class="dropdown-check-btn">Alle Klassen</button>
            <div class="dropdown-check-panel dropdown-check-panel-left"></div>
          </div>
          <div class="dropdown-check hidden" id="filter-kreisverband">
            <button type="button" class="dropdown-check-btn">Alle Kreisverbände</button>
            <div class="dropdown-check-panel dropdown-check-panel-left"></div>
          </div>
          <div class="dropdown-check suggest-input planning-search" id="filter-student">
            <input type="search" data-readable="true" placeholder="Schüler suchen" aria-label="Schüler suchen" autocomplete="off" />
            <div class="dropdown-check-panel dropdown-check-panel-left"></div>
          </div>
        </div>
        <div style="display:flex;gap:.25rem">
          <button class="btn btn-secondary" id="scroll-left" style="padding:.375rem .75rem;font-size:1rem;line-height:1">&#8249;</button>
          <button class="btn btn-secondary" id="scroll-right" style="padding:.375rem .75rem;font-size:1rem;line-height:1">&#8250;</button>
        </div>
      </div>
      <div id="planning-grid"></div>
    </div>`;

  // Verwalten-Menü und Auto-Planung existieren nur mit Schreibrechten
  if (canWrite()) {
    el.querySelector("#btn-auto-plan")!.addEventListener("click", () => openAutoPlanModal(el));

    const ddManage = el.querySelector("#dd-manage")!;
    ddManage.querySelector(".dropdown-check-btn")!.addEventListener("click", () => ddManage.classList.toggle("open"));
    document.addEventListener("click", (e) => { if (!ddManage.contains(e.target as Node)) ddManage.classList.remove("open"); }, { signal: ac.signal });
    el.querySelector("#add-vacation")!.addEventListener("click", () => { ddManage.classList.remove("open"); openVacationForm(); });
    el.querySelector("#add-period")!.addEventListener("click", () => { ddManage.classList.remove("open"); openPeriodForm(); });
    el.querySelector("#edit-period")!.addEventListener("click", () => { ddManage.classList.remove("open"); openEditPeriodForm(el); });
    el.querySelector("#del-period")!.addEventListener("click", () => { ddManage.classList.remove("open"); openDeletePeriodForm(el); });
    el.querySelector("#del-vacation")!.addEventListener("click", () => { ddManage.classList.remove("open"); openDeleteVacationForm(el); });
    el.querySelector("#make-all-visible")!.addEventListener("click", async () => {
      ddManage.classList.remove("open");
      if (!activePeriod) return;
      const p = activePeriod;
      const ok = await confirmDialog({
        title: "Alle sichtbar machen",
        message: `Alle Einsätze im Zeitraum „${p.name}" für Schüler sichtbar machen?`,
        confirmLabel: "Sichtbar machen",
      });
      if (!ok) return;
      try {
        const { updated } = await api.periods.makeAllVisible(p.id);
        assignments = await api.assignments.list(p.id);
        renderGrid(el);
        showToast(`${updated} Einsätze sind jetzt für Schüler sichtbar.`);
      } catch (err: any) { showToast(`Fehlgeschlagen: ${err.message}`); }
    });
    el.querySelector("#confirm-all")!.addEventListener("click", async () => {
      ddManage.classList.remove("open");
      if (!activePeriod) return;
      const p = activePeriod;
      const ok = await confirmDialog({
        title: "Alle als bestätigt markieren",
        message: `Alle Einsätze im Zeitraum „${p.name}" als bestätigt markieren?`,
        confirmLabel: "Bestätigen",
      });
      if (!ok) return;
      try {
        const { updated } = await api.periods.confirmAll(p.id);
        assignments = await api.assignments.list(p.id);
        renderGrid(el);
        showToast(`${updated} Einsätze sind jetzt bestätigt.`);
      } catch (err: any) { showToast(`Fehlgeschlagen: ${err.message}`); }
    });
  }

  // Rein lesende Ansicht, deshalb bewusst ohne writeOnly: auch Betrachter duerfen sie
  // sehen. Die Matrix liegt seit dem Auswertungsbereich fest auf einer eigenen Seite
  // statt in einem Fenster; der Knopf fuehrt nur noch dorthin.
  el.querySelector("#btn-progress")!.addEventListener("click", () => {
    window.location.hash = "#/auswertungen?bereich=praktikum";
  });

  // Ansicht: rein lesend, deshalb auch für Betrachter. Wirkt sofort, ohne Speichern-Knopf.
  const ddAnsicht = el.querySelector("#dd-ansicht")!;
  const markAnsicht = () => ddAnsicht.querySelectorAll<HTMLElement>(".dropdown-check-item").forEach(i => {
    i.classList.toggle("is-active", i.dataset.breite === ansicht.breite || i.dataset.namen === ansicht.namen);
  });
  markAnsicht();
  ddAnsicht.querySelector(".dropdown-check-btn")!.addEventListener("click", () => ddAnsicht.classList.toggle("open"));
  document.addEventListener("click", (e) => { if (!ddAnsicht.contains(e.target as Node)) ddAnsicht.classList.remove("open"); }, { signal: ac.signal });
  ddAnsicht.querySelectorAll<HTMLElement>(".dropdown-check-item").forEach(item => item.addEventListener("click", () => {
    ddAnsicht.classList.remove("open");
    const wrapper = el.querySelector(".planning-wrapper") as HTMLElement | null;
    // Der erste sichtbare Schüler bleibt vorn, auch wenn sich die Spaltenbreite ändert
    const firstCol = cellwise ? cellwise.c0 : Math.round((wrapper?.scrollLeft ?? 0) / colW());
    if (item.dataset.breite) ansicht.breite = item.dataset.breite as Spaltenbreite;
    if (item.dataset.namen) ansicht.namen = item.dataset.namen as Namensfolge;
    saveAnsicht();
    markAnsicht();
    renderGrid(el, { left: Math.max(0, firstCol) * colW(), top: wrapper?.scrollTop ?? 0 });
  }));

  const ddDownload = el.querySelector("#dd-download")!;
  ddDownload.querySelector(".dropdown-check-btn")!.addEventListener("click", () => ddDownload.classList.toggle("open"));
  document.addEventListener("click", (e) => { if (!ddDownload.contains(e.target as Node)) ddDownload.classList.remove("open"); }, { signal: ac.signal });

  function getExportData(): PlanningExportData | null {
    if (!activePeriod) { showToast("Bitte zuerst einen Zeitraum wählen."); return null; }
    // Downloads folgen den gesetzten Filtern; die zuweisungsbasierten Exporte
    // (Prakt.stellen, Kreisverband) brauchen dafür ebenfalls die eingegrenzte Liste.
    const shown = visibleStudents();
    const ids = new Set(shown.map(s => +s.id));
    const shownAssignments = assignments.filter(a => ids.has(+a.studentId));
    return { period: activePeriod, students: shown, assignments: shownAssignments, vacations, departments, classes, practices, abrechnungEnabled };
  }
  const exportItems: [string, (fmt: "xlsx" | "pdf", d: PlanningExportData) => Promise<void>, "xlsx" | "pdf"][] = [
    ["export-gesamt-xlsx",   exportGesamt,            "xlsx"],
    ["export-gesamt-pdf",    exportGesamt,            "pdf"],
    ["export-kv-xlsx",       exportKreisverband,      "xlsx"],
    ["export-kv-pdf",        exportKreisverband,      "pdf"],
  ];
  for (const [id, fn, fmt] of exportItems) {
    el.querySelector(`#${id}`)!.addEventListener("click", () => {
      ddDownload.classList.remove("open");
      const d = getExportData();
      if (d) fn(fmt, d);
    });
  }
  // „Nach Schülern“ fragt vorher, ob die Kontaktdaten der Stellen mit in die Datei sollen
  for (const fmt of ["xlsx", "pdf"] as const) {
    el.querySelector(`#export-schueler-${fmt}`)!.addEventListener("click", () => {
      ddDownload.classList.remove("open");
      if (getExportData()) openSchuelerExportModal(fmt, getExportData);
    });
  }
  // „Nach Prakt.stellen“ fragt vorher, ob die Stammdaten der Schüler mit in die Datei sollen
  for (const fmt of ["xlsx", "pdf"] as const) {
    el.querySelector(`#export-prakt-${fmt}`)!.addEventListener("click", () => {
      ddDownload.classList.remove("open");
      if (getExportData()) openStellenExportModal(fmt, getExportData);
    });
  }

  await loadAll();
  if (ac.signal.aborted) return;
  // Vorschau aus einem früheren Besuch der Seite nur behalten, wenn ihr Zeitraum noch der gewählte ist
  if (previewAssignments && previewPeriodId !== (activePeriod ? +activePeriod.id : null)) clearPreview();

  if (vorplanungEnabled && canWrite()) {
    el.querySelector("#vorplanung-divider")!.classList.remove("hidden");
    el.querySelector("#make-all-visible")!.classList.remove("hidden");
    el.querySelector("#confirm-all")!.classList.remove("hidden");
  }

  // Export-Option "Nach Kreisverband" nur für freigeschaltete DRK-Schulen
  if (kreisverbandEnabled) {
    el.querySelector("#export-kv-divider")!.classList.remove("hidden");
    el.querySelector("#export-kv-xlsx")!.classList.remove("hidden");
    el.querySelector("#export-kv-pdf")!.classList.remove("hidden");
  }

  const pDrop = el.querySelector("#filter-period") as HTMLElement;
  periodDropdown = initDropdownSelect(pDrop, async (v) => {
    const p = periods.find(x => +x.id === +v);
    if (!p) return;
    // Die Berechnung dauert; ein versehentlicher Wechsel soll die Vorschau nicht still wegwerfen
    if (previewAssignments && previewPeriodId !== +p.id) {
      const previewPeriod = periods.find(x => +x.id === previewPeriodId);
      const ok = await confirmDialog({
        title: "Vorschau verwerfen?",
        message: `Die berechnete Vorschau der automatischen Planung gehört zu „${previewPeriod?.name ?? activePeriod?.name ?? ""}" und geht beim Wechsel verloren.`,
        confirmLabel: "Verwerfen und wechseln",
      });
      if (!ok) { if (activePeriod) periodDropdown?.setValue(String(activePeriod.id)); return; }
      clearPreview();
    }
    // Sofort sichtbar machen, dass geladen wird; bei großen Zeiträumen dauert das Raster
    const grid = el.querySelector("#planning-grid")!;
    grid.innerHTML = `<div style="display:flex;align-items:center;justify-content:center;height:200px"><div class="spinner"></div></div>`;
    try {
      if (!(await loadPeriodData(p))) return;
    } catch (err: any) {
      showToast(`Zeitraum konnte nicht geladen werden: ${err.message}`);
      periodDropdown?.setValue(activePeriod ? String(activePeriod.id) : "");
      renderGrid(el);
      return;
    }
    applyPeriodClassDefault(p); renderGrid(el);
  });
  fillPeriodDropdown(el);

  // Klassen-Filter als Multiselect; filtert lokal, kein Nachladen nötig.
  // "Ohne Klasse" nur anbieten, wenn es überhaupt Schüler ohne Klassenzuordnung gibt.
  const cDrop = el.querySelector("#filter-class") as HTMLElement;
  const cPanel = cDrop.querySelector(".dropdown-check-panel") as HTMLElement;
  const classItems = classes.map(c => `<label class="dropdown-check-item"><input type="checkbox" value="${c.id}" /> ${esc(c.name)}</label>`);
  if (students.some(s => !s.classId)) {
    classItems.push(`<div class="dropdown-divider"></div><label class="dropdown-check-item"><input type="checkbox" value="none" /> Ohne Klasse</label>`);
  }
  cPanel.innerHTML = classItems.join("");
  classDropdown = initDropdownCheck(cDrop, (vals) => {
    filterClasses = vals.map(v => (v === "none" ? "none" : +v));
    dropStudentSearchIfFiltered();
    renderGrid(el);
  }, { allSelectedShowsDefault: true });
  applyPeriodClassDefault(activePeriod);

  // Kreisverband-Filter nur für freigeschaltete DRK-Schulen; filtert lokal, kein Nachladen nötig
  const kDrop = el.querySelector("#filter-kreisverband") as HTMLElement;
  if (kreisverbandEnabled) {
    kDrop.classList.remove("hidden");
    (kDrop.querySelector(".dropdown-check-panel") as HTMLElement).innerHTML =
      `<div class="dropdown-check-item" data-value="">Alle Kreisverbände</div>`
      + kreisverbaende.map(k => `<div class="dropdown-check-item" data-value="${k.id}">${esc(k.name)}</div>`).join("")
      + `<div class="dropdown-divider"></div><div class="dropdown-check-item" data-value="none">Ohne Kreisverband</div>`;
    const kd = initDropdownSelect(kDrop, (v) => {
      filterKreisverband = v === "" ? undefined : v === "none" ? "none" : +v;
      dropStudentSearchIfFiltered();
      renderGrid(el);
    });
    kd.setValue(filterKreisverband === undefined ? "" : String(filterKreisverband));
  }

  // Schülersuche: schlägt nur Schüler aus Klassen- und Kreisverband-Filter vor und grenzt
  // das Raster erst ein, wenn ein Name gewählt ist. Beim Tippen bleibt das Raster stehen.
  const className = new Map(classes.map(c => [+c.id, c.name]));
  studentSearch = initSuggestInput(el.querySelector("#filter-student") as HTMLElement, {
    items: () => filteredStudents().map(s => ({
      value: String(s.id),
      label: studentLabel(s),
      sub: s.classId ? className.get(+s.classId) ?? "" : "Ohne Klasse",
    })),
    onSelect: (v) => { filterStudentId = v === null ? null : +v; renderGrid(el); },
  });

  // Ein Klick irgendwo schliesst die offenen Filter-Dropdowns. Die Menues im
  // Seitenkopf bringen ihre eigenen Listener mit.
  document.addEventListener("click", () => {
    el.querySelectorAll(".filter-bar .dropdown-check.open").forEach(d => d.classList.remove("open"));
  }, { signal: ac.signal });

  renderGrid(el);

  el.querySelector("#scroll-left")!.addEventListener("click", () => scrollGridSideways(el, -1));
  el.querySelector("#scroll-right")!.addEventListener("click", () => scrollGridSideways(el, 1));
}

// ── Download „Nach Schülern“ ──

const SCHUELER_EXPORT_KEY = "planning.exportSchuelerKontakte";
const CONTACT_FIELDS: [keyof SchuelerContactOptions, string][] = [
  ["address", "Adresse"], ["person", "Kontaktperson"], ["phone", "Telefon"], ["email", "E-Mail"], ["comment", "Kommentar"],
];

/** Letzte Wahl aus diesem Browser. Beim ersten Mal aus, die Felder aber alle angehakt. */
function loadSchuelerExportChoice(): { enabled: boolean; fields: SchuelerContactOptions } {
  const fallback = { enabled: false, fields: { address: true, person: true, phone: true, email: true, comment: true } };
  try {
    const raw = JSON.parse(localStorage.getItem(SCHUELER_EXPORT_KEY) || "null");
    if (!raw || typeof raw !== "object") return fallback;
    const fields = { ...fallback.fields };
    for (const [key] of CONTACT_FIELDS) if (typeof raw.fields?.[key] === "boolean") fields[key] = raw.fields[key];
    return { enabled: raw.enabled === true, fields };
  } catch { return fallback; }
}

function saveSchuelerExportChoice(choice: { enabled: boolean; fields: SchuelerContactOptions }) {
  try { localStorage.setItem(SCHUELER_EXPORT_KEY, JSON.stringify(choice)); } catch { /* ohne Speicher eben ohne Vorbelegung */ }
}

function openSchuelerExportModal(fmt: "xlsx" | "pdf", getData: () => PlanningExportData | null) {
  const choice = loadSchuelerExportChoice();
  openModal(`Download nach Schülern (${fmt === "pdf" ? "PDF" : "Excel"})`, `
    <label class="checkbox-label"><input type="checkbox" id="f-sx-contacts" ${choice.enabled ? "checked" : ""} /> Kontaktdaten der Praktikumsstellen mitnehmen</label>
    <small class="text-muted" style="display:block;margin:.25rem 0 .75rem 1.5rem">Aus dem Bereich „Informationen für Schüler“ der Praktikumsstelle. Der Name der Stelle ist immer dabei.</small>
    <div id="f-sx-fields" style="display:flex;flex-direction:column;gap:.5rem;padding-left:1.5rem">
      ${CONTACT_FIELDS.map(([key, label]) => `<label class="checkbox-label"><input type="checkbox" data-field="${key}" ${choice.fields[key] ? "checked" : ""} /> ${label}</label>`).join("")}
    </div>
    <div class="alert alert-error hidden" id="f-modal-error"></div>
    <div class="form-actions">
      <button class="btn btn-secondary" id="f-cancel">Abbrechen</button>
      <button class="btn btn-primary" id="f-sx-run">Herunterladen</button>
    </div>`);

  const mb = modalBody();
  const master = mb.querySelector("#f-sx-contacts") as HTMLInputElement;
  const fieldInputs = Array.from(mb.querySelectorAll<HTMLInputElement>("#f-sx-fields input[data-field]"));
  const syncFields = () => {
    for (const input of fieldInputs) {
      input.disabled = !master.checked;
      (input.closest("label") as HTMLElement).style.opacity = master.checked ? "" : ".5";
    }
  };
  master.addEventListener("change", syncFields);
  syncFields();

  mb.querySelector("#f-cancel")!.addEventListener("click", closeModal);
  const runBtn = mb.querySelector("#f-sx-run") as HTMLButtonElement;
  runBtn.addEventListener("click", async () => {
    hideModalError(mb);
    const fields = { ...choice.fields };
    for (const input of fieldInputs) fields[input.dataset.field as keyof SchuelerContactOptions] = input.checked;
    saveSchuelerExportChoice({ enabled: master.checked, fields });

    const data = getData();
    if (!data) return;
    if (!master.checked) { closeModal(); await exportSchueler(fmt, data); return; }

    // Die Seite kennt nur aktive Stellen. Für Einsätze an inzwischen deaktivierten
    // Stellen fehlten sonst die Kontaktdaten, deshalb hier alle frisch laden.
    runBtn.disabled = true;
    let allPractices: Practice[];
    try { allPractices = await api.practices.list(undefined, true); }
    catch (err: any) { runBtn.disabled = false; showModalError(mb, `Praktikumsstellen konnten nicht geladen werden: ${err.message}`); return; }
    closeModal();
    await exportSchueler(fmt, { ...data, practices: allPractices }, fields);
  });
}

// ── Download „Nach Prakt.stellen“ ──

const STELLEN_EXPORT_KEY = "planning.exportStellenStammdaten";
const MASTER_DATA_FIELDS: [keyof StudentMasterDataOptions, string][] = [
  ["address", "Adresse"], ["phone", "Telefon"], ["email", "E-Mail"],
];

/** Letzte Wahl aus diesem Browser, ohne die Bestätigung. Beim ersten Mal aus, die Felder alle angehakt. */
function loadStellenExportChoice(): { enabled: boolean; fields: StudentMasterDataOptions } {
  const fallback = { enabled: false, fields: { address: true, phone: true, email: true } };
  try {
    const raw = JSON.parse(localStorage.getItem(STELLEN_EXPORT_KEY) || "null");
    if (!raw || typeof raw !== "object") return fallback;
    const fields = { ...fallback.fields };
    for (const [key] of MASTER_DATA_FIELDS) if (typeof raw.fields?.[key] === "boolean") fields[key] = raw.fields[key];
    return { enabled: raw.enabled === true, fields };
  } catch { return fallback; }
}

function saveStellenExportChoice(choice: { enabled: boolean; fields: StudentMasterDataOptions }) {
  try { localStorage.setItem(STELLEN_EXPORT_KEY, JSON.stringify(choice)); } catch { /* ohne Speicher eben ohne Vorbelegung */ }
}

function openStellenExportModal(fmt: "xlsx" | "pdf", getData: () => PlanningExportData | null) {
  const choice = loadStellenExportChoice();
  openModal(`Download nach Prakt.stellen (${fmt === "pdf" ? "PDF" : "Excel"})`, `
    <label class="checkbox-label"><input type="checkbox" id="f-px-master" ${choice.enabled ? "checked" : ""} /> Stammdaten der Schüler mitnehmen</label>
    <small class="text-muted" style="display:block;margin:.25rem 0 .75rem 1.5rem">Name und Klasse sind immer dabei.</small>
    <div id="f-px-fields" style="display:flex;flex-direction:column;gap:.5rem;padding-left:1.5rem">
      ${MASTER_DATA_FIELDS.map(([key, label]) => `<label class="checkbox-label"><input type="checkbox" data-field="${key}" ${choice.fields[key] ? "checked" : ""} /> ${label}</label>`).join("")}
    </div>
    <div id="f-px-confirm-box" class="hidden" style="margin-top:1rem">
      <label class="checkbox-label"><input type="checkbox" id="f-px-confirm" /> Ich bestätige, dass die Weitergabe dieser Schülerdaten an die Praktikumsstellen zulässig ist.</label>
      <small class="text-muted" style="display:block;margin:.25rem 0 0 1.5rem">Der Download wird mit deinem Namen protokolliert.</small>
    </div>
    <div class="alert alert-error hidden" id="f-modal-error"></div>
    <div class="form-actions">
      <button class="btn btn-secondary" id="f-cancel">Abbrechen</button>
      <button class="btn btn-primary" id="f-px-run">Herunterladen</button>
    </div>`);

  const mb = modalBody();
  const master = mb.querySelector("#f-px-master") as HTMLInputElement;
  const confirmBox = mb.querySelector("#f-px-confirm-box") as HTMLElement;
  const confirm = mb.querySelector("#f-px-confirm") as HTMLInputElement;
  const runBtn = mb.querySelector("#f-px-run") as HTMLButtonElement;
  const fieldInputs = Array.from(mb.querySelectorAll<HTMLInputElement>("#f-px-fields input[data-field]"));
  const sync = () => {
    for (const input of fieldInputs) {
      input.disabled = !master.checked;
      (input.closest("label") as HTMLElement).style.opacity = master.checked ? "" : ".5";
    }
    confirmBox.classList.toggle("hidden", !master.checked);
    // Mit Stammdaten erst, wenn mindestens ein Feld gewählt und die Weitergabe bestätigt ist
    runBtn.disabled = master.checked && (!fieldInputs.some(i => i.checked) || !confirm.checked);
  };
  master.addEventListener("change", sync);
  confirm.addEventListener("change", sync);
  for (const input of fieldInputs) input.addEventListener("change", sync);
  sync();

  mb.querySelector("#f-cancel")!.addEventListener("click", closeModal);
  runBtn.addEventListener("click", async () => {
    hideModalError(mb);
    const fields = { ...choice.fields };
    for (const input of fieldInputs) fields[input.dataset.field as keyof StudentMasterDataOptions] = input.checked;
    saveStellenExportChoice({ enabled: master.checked, fields });

    const data = getData();
    if (!data) return;
    if (!master.checked) { closeModal(); await exportPraktikumsstellen(fmt, data); return; }

    // Erst protokollieren, dann herunterladen. Scheitert das Protokoll, gibt es keine Datei.
    const stellenAssignments = data.assignments.filter(a => !a.isFreeInput && a.practiceId);
    runBtn.disabled = true;
    try {
      await api.downloadLog.create({
        periodId: +data.period.id,
        export: "praktikumsstellen",
        format: fmt,
        fields: MASTER_DATA_FIELDS.map(([key]) => key).filter(key => fields[key]),
        studentCount: new Set(stellenAssignments.map(a => +a.studentId)).size,
        practiceCount: new Set(stellenAssignments.map(a => +a.practiceId!)).size,
      });
    } catch (err: any) {
      runBtn.disabled = false;
      showModalError(mb, `Der Download konnte nicht protokolliert werden und wurde deshalb nicht erstellt: ${err.message}`);
      return;
    }
    closeModal();
    await exportPraktikumsstellen(fmt, data, fields);
  });
}

/** Meldung im offenen Modal anzeigen. Dort gehört sie hin: das Fenster bleibt stehen,
 *  ein Toast am Bildschirmrand würde daneben leicht übersehen. */
function showModalError(mb: HTMLElement, message: string) {
  const box = mb.querySelector("#f-modal-error") as HTMLElement | null;
  if (!box) { showToast(message); return; }
  box.textContent = message;
  box.classList.remove("hidden");
}

function hideModalError(mb: HTMLElement) {
  mb.querySelector("#f-modal-error")?.classList.add("hidden");
}

function showToast(message: string) {
  const toast = document.createElement("div");
  toast.className = "toast";
  toast.textContent = message;
  document.body.appendChild(toast);
  requestAnimationFrame(() => toast.classList.add("toast--visible"));
  setTimeout(() => {
    toast.classList.remove("toast--visible");
    setTimeout(() => toast.remove(), 300);
  }, 2500);
}
